import axios, { type InternalAxiosRequestConfig } from 'axios';

import { publishConsentOutdated } from '@/services/consent-events';
import type {
  AccessLogEntry,
  AccessLogExportJob,
  AnalyticsSummary,
  Announcement,
  AppNotification,
  BrandingConfig,
  ChangeShiftBody,
  CheckedInVisitor,
  ConfirmEntryResult,
  CreateAnnouncementBody,
  CreateDeliveryPassBody,
  CreateBulkInvitationBody,
  CreateBulkInvitationResult,
  CreateInvitationBody,
  CreateOrgAdminBody,
  CreateOrgAdminResult,
  CreateOrgPropertyBody,
  CreateRegistrationLinkBody,
  RegistrationLinkResult,
  CreateRecurringPassBody,
  CreateRosterBody,
  CreateShiftTemplateBody,
  CreateTaxiPassBody,
  CreateWalkInBody,
  CreateWorkerPassBody,
  CursorPaginatedResponse,
  DeliveryPass,
  DeliveryPassWithPin,
  DsarQueueItem,
  DsarRequest,
  DsarType,
  PmDsarTransition,
  EnterpriseEmployee,
  EnterpriseTenant,
  Favourite,
  Gate,
  GateTrafficPoint,
  GuardActivityEvent,
  GuardDailyLogParams,
  GuardDevice,
  HeatmapCell,
  IncidentDetail,
  IncidentResult,
  IncidentStatus,
  IncidentSummary,
  Invitation,
  InvitationType,
  InvitationPreview,
  InviteGuardBody,
  InviteResidentBody,
  InviteSupervisorBody,
  MeProfile,
  NotificationPreferences,
  OrgAdmin,
  OrgDashboard,
  OrgProperty,
  PaginatedResponse,
  PendingRegistration,
  PinValidationResult,
  PmAccessLogEntry,
  PmAccessLogFilters,
  PmUser,
  PmUserDetail,
  PmUserStatus,
  PmWalkInRequest,
  PropertyConfig,
  PropertyGate,
  PropertyModule,
  PropertyStats,
  RecurrencePattern,
  RecurringPass,
  RosterEntry,
  ShiftInfo,
  ShiftTemplate,
  StartShiftBody,
  TaxiPass,
  TaxiPassWithPin,
  Unit,
  UpcomingVisitor,
  UpdateShiftTemplateBody,
  UserRole,
  VisitorTypeBreakdown,
  WalkInRequest,
  WorkerPass,
  DeviceSession,
  EmergencyType,
  LockdownStatus,
  LockdownRecord,
  Organisation,
  CreateOrganisationBody,
  AdminError,
  AdminHealth,
  BullMQQueue,
  AdminIncidentSummary,
  AuditLogEntry,
  AuditLogFilters,
  FeatureFlag,
  UpdateFeatureFlagsBody,
  PlatformConfig,
  UpdatePlatformConfigBody,
  ImpersonationSession,
  StartImpersonationBody,
  OrgUser,
  AccessEvent,
  VisitFlag,
} from '@/types';

/**
 * Central Axios instance for all API calls.
 *
 * - Base URL from VITE_API_BASE_URL env var (set in .env.local for dev)
 * - Sends cookies (withCredentials) for session cookie transport
 * - Normalises error responses to a typed ApiError
 * ADR-001: Never store tokens in localStorage — auth lives in HttpOnly cookies.
 */
export const apiClient = axios.create({
  baseURL: import.meta.env['VITE_API_BASE_URL'] ?? '/api/v1',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ---------------------------------------------------------------------------
// Active property context — set by AuthContext once the resident session loads.
// TenantMiddleware resolves the tenant from the subdomain in production (e.g.
// runda.securegate.com). In local dev on plain localhost there is no subdomain,
// so we inject X-Property-Id so the middleware can identify the tenant.
// ---------------------------------------------------------------------------
let _activePropertyId: string | null = null;

export function setActivePropertyId(id: string | null) {
  _activePropertyId = id;
}

const STATE_CHANGING_METHODS = new Set(['POST', 'PATCH', 'PUT', 'DELETE']);
const CSRF_COOKIE_NAME = 'csrf_token';

// Mirrors the server-side CSRF_BYPASS_PREFIXES in csrf.middleware.ts.
// These routes either issue the CSRF cookie (login, MFA verify) or are
// server-to-server / unauthenticated — they run before a session exists and
// must not be blocked by the client-side CSRF guard.
const CSRF_BYPASS_PREFIXES = [
  '/auth/login',
  '/auth/mfa/verify',
  '/auth/password/reset-request',
  '/auth/password/reset',
  '/auth/device/',
  '/guard/',
  '/public/',
  '/health',
];

/**
 * Read the value of a cookie. Returns null when the cookie is absent.
 * Used only for the CSRF cookie — every other auth cookie is HttpOnly and
 * unreadable from JS by design (ADR-001).
 */
function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp('(?:^|; )' + escaped + '=([^;]*)'));
  return match ? decodeURIComponent(match[1] as string) : null;
}

apiClient.interceptors.request.use((config) => {
  if (_activePropertyId) {
    config.headers['X-Property-Id'] = _activePropertyId;
  }

  // Mode B device session — send as header since cross-origin cookies are unreliable
  const deviceSession = localStorage.getItem('sg_device_session');
  if (deviceSession) {
    config.headers['X-Device-Session'] = deviceSession;
  }

  // CSRF double-submit: mirror the csrf_token cookie value into the
  // X-CSRF-Token header on every state-changing request. The server's
  // CsrfMiddleware enforces a timing-safe match (security-model.md §10).
  const method = (config.method ?? 'get').toUpperCase();
  if (STATE_CHANGING_METHODS.has(method)) {
    const url = config.url ?? '';
    // Pre-session routes bypass CSRF (the server does the same). These routes
    // either issue the cookie (login) or run before any session exists.
    const isBypass = CSRF_BYPASS_PREFIXES.some((p) => url.startsWith(p));
    const csrfToken = readCookie(CSRF_COOKIE_NAME);
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    } else if (!isBypass) {
      // Cookie absent on a protected route — session has expired or cookies
      // were cleared. Reject with a legible message; the server would return
      // a silent 403 which would be harder for the user to understand.
      return Promise.reject({
        status: 403,
        code: 'CSRF_TOKEN_MISSING',
        message: 'Your session appears to have expired. Please refresh the page.',
        requestId: undefined,
      });
    }
  }
  return config;
});

// ---------------------------------------------------------------------------
// 401 interceptor — automatic token refresh.
//
// When the access_token cookie expires (default 15 min), the server returns
// 401. This interceptor transparently calls POST /auth/token/refresh (which
// uses the refresh_token cookie, scoped to that path) to obtain a new
// access_token, then retries the original request.
//
// If the refresh itself fails (e.g. refresh token expired or revoked), the
// error propagates normally — AuthProvider's useQuery will see the 401 and
// RoleProtectedRoute will redirect to /login.
// ---------------------------------------------------------------------------
let _refreshPromise: Promise<void> | null = null;

apiClient.interceptors.response.use(
  (response) => {
    // KDPA §32 / ToS §13 — server stamps X-Consent-Outdated on every response
    // when the resident's accepted versions are stale. Surface to the
    // ConsentReacceptanceProvider via the pub/sub bridge.
    const header = response.headers?.['x-consent-outdated'];
    if (typeof header === 'string' && header.length > 0) {
      publishConsentOutdated(header);
    }
    return response;
  },
  async (error: unknown) => {
    if (!axios.isAxiosError(error) || !error.response) {
      return Promise.reject(error);
    }

    const originalRequest = error.config as InternalAxiosRequestConfig & { _retried?: boolean };

    // Only attempt refresh on 401, and only once per request.
    // Skip refresh for auth endpoints themselves to avoid loops.
    if (
      error.response.status === 401 &&
      !originalRequest._retried &&
      !originalRequest.url?.startsWith('/auth/')
    ) {
      originalRequest._retried = true;

      // Coalesce concurrent refresh attempts into a single request.
      _refreshPromise ??= apiClient
        .post('/auth/token/refresh')
        .then(() => {})
        .finally(() => {
          _refreshPromise = null;
        });

      try {
        await _refreshPromise;
        // Retry the original request with the new access_token cookie.
        return apiClient(originalRequest);
      } catch {
        // Refresh failed — fall through to the normal error handler below.
      }
    }

    const data = error.response.data as {
      message?: string;
      code?: string;
      requestId?: string;
    };

    return Promise.reject({
      status: error.response.status,
      code: data.code ?? 'UNKNOWN_ERROR',
      message: data.message ?? error.message,
      requestId: data.requestId,
    });
  },
);

type ListEnvelope<T> = PaginatedResponse<T> | T[];

type RecurringPassListItem = Partial<RecurringPass> & {
  visitor_name?: string;
  pass_type?: string;
  rrule?: string;
  window_start_time?: string;
  window_end_time?: string;
  valid_from?: string;
  valid_until?: string | null;
  created_at?: string;
  worker_name?: string;
  work_days?: string[];
  enc_worker_phone?: string;
};

type InvitationListItem = Partial<Invitation> & {
  visitor_name?: string;
  invitation_type?: string;
  valid_from?: string;
  valid_until?: string;
  gate_id?: string | null;
  gate_name?: string | null;
  notes?: string | null;
  party_size?: number;
  vehicle_plate?: string | null;
  vehicle_make?: string | null;
  vehicle_colour?: string | null;
  qr_token?: string | null;
  visitor_confirmed?: boolean | null;
  created_at?: string;
};

const RRULE_DAY_TO_INDEX: Record<string, number> = {
  SU: 0,
  MO: 1,
  TU: 2,
  WE: 3,
  TH: 4,
  FR: 5,
  SA: 6,
};

const INDEX_TO_RRULE_DAY = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'] as const;

function unwrapListEnvelope<T>(payload: ListEnvelope<T>): T[] {
  return Array.isArray(payload) ? payload : payload.data;
}

function parseRRuleScheduleDays(rrule?: string): number[] | null {
  if (!rrule) return null;
  const byDay = /(?:^|;)BYDAY=([^;]+)/.exec(rrule)?.[1];
  if (!byDay) return null;

  const days = byDay
    .split(',')
    .map((token) => RRULE_DAY_TO_INDEX[token.trim().toUpperCase()])
    .filter((value): value is number => value !== undefined);

  return days.length > 0 ? days : null;
}

function deriveRecurrencePattern(
  rrule: string | undefined,
  scheduleDays: number[] | null,
): RecurrencePattern {
  if (rrule?.includes('FREQ=DAILY')) return 'daily';
  if (scheduleDays?.join(',') === '1,2,3,4,5') return 'weekdays';
  if (scheduleDays && scheduleDays.length > 0) return 'specific_days';
  return 'custom';
}

function recurrencePatternToRRule(pattern: RecurrencePattern, scheduleDays?: number[]): string {
  switch (pattern) {
    case 'daily':
      return 'FREQ=DAILY';
    case 'weekdays':
      return 'FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR';
    case 'specific_days': {
      const days =
        scheduleDays
          ?.map((day) => INDEX_TO_RRULE_DAY[day])
          .filter((value): value is (typeof INDEX_TO_RRULE_DAY)[number] => value !== undefined) ??
        [];
      return days.length > 0 ? `FREQ=WEEKLY;BYDAY=${days.join(',')}` : 'FREQ=WEEKLY;BYDAY=MO';
    }
    case 'custom':
    default:
      return 'FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR';
  }
}

function recurrencePatternToWorkDays(
  pattern: RecurrencePattern,
  scheduleDays?: number[],
): number[] {
  switch (pattern) {
    case 'daily':
      return [0, 1, 2, 3, 4, 5, 6];
    case 'weekdays':
      return [1, 2, 3, 4, 5];
    case 'specific_days':
      return scheduleDays && scheduleDays.length > 0 ? scheduleDays : [1, 2, 3, 4, 5];
    case 'custom':
    default:
      return scheduleDays && scheduleDays.length > 0 ? scheduleDays : [1, 2, 3, 4, 5];
  }
}

function normalizeRecurringPass(
  raw: RecurringPassListItem,
  defaultPassType: 'recurring' | 'domestic_worker',
): RecurringPass {
  const scheduleDays =
    raw.scheduleDays ??
    (Array.isArray(raw.work_days) ? raw.work_days.map((day) => Number(day)) : null) ??
    parseRRuleScheduleDays(raw.rrule);

  return {
    id: raw.id ?? '',
    visitorName: raw.visitorName ?? raw.visitor_name ?? raw.worker_name ?? 'Unknown visitor',
    visitorPhone: raw.visitorPhone ?? '',
    recurrencePattern: deriveRecurrencePattern(raw.rrule, scheduleDays),
    scheduleDays,
    entryTimeStart: raw.entryTimeStart ?? raw.window_start_time ?? '',
    entryTimeEnd: raw.entryTimeEnd ?? raw.window_end_time ?? '',
    validFrom: raw.validFrom ?? raw.valid_from ?? '',
    validUntil: raw.validUntil ?? raw.valid_until ?? null,
    gateId: raw.gateId ?? '',
    gateName: raw.gateName ?? 'Any gate',
    status: raw.status ?? 'active',
    notes: raw.notes ?? null,
    passType:
      raw.passType === 'domestic_worker' || raw.pass_type === 'domestic_worker'
        ? 'domestic_worker'
        : defaultPassType,
    createdAt: raw.createdAt ?? raw.created_at ?? '',
  };
}

function buildCreatedRecurringPass(
  raw: Partial<RecurringPass> & {
    id?: string;
    status?: string;
    validFrom?: string;
    validUntil?: string | null;
    createdAt?: string;
  },
  source: CreateRecurringPassBody,
  passType: 'recurring' | 'domestic_worker',
): RecurringPass {
  const scheduleDays = recurrencePatternToWorkDays(source.recurrencePattern, source.scheduleDays);

  return {
    id: raw.id ?? '',
    visitorName: source.visitorName,
    visitorPhone: source.visitorPhone,
    recurrencePattern: source.recurrencePattern,
    scheduleDays,
    entryTimeStart: source.entryTimeStart,
    entryTimeEnd: source.entryTimeEnd,
    validFrom: raw.validFrom ?? source.validFrom,
    validUntil: raw.validUntil ?? source.validUntil ?? null,
    gateId: source.gateId ?? '',
    gateName: 'Any gate',
    status: (raw.status as RecurringPass['status'] | undefined) ?? 'active',
    notes: source.notes ?? null,
    passType,
    createdAt: raw.createdAt ?? new Date().toISOString(),
  };
}

function normalizeInvitation(raw: InvitationListItem): Invitation {
  const status = (raw.status as Invitation['status'] | undefined) ?? 'active';

  return {
    id: raw.id ?? '',
    visitorName: raw.visitorName ?? raw.visitor_name ?? 'Unknown visitor',
    visitorPhone: raw.visitorPhone ?? '',
    visitorEmail: raw.visitorEmail ?? null,
    invitationType:
      (raw.invitationType as Invitation['invitationType'] | undefined) ??
      (raw.invitation_type as Invitation['invitationType'] | undefined) ??
      'single_visit',
    status,
    validFrom: raw.validFrom ?? raw.valid_from ?? '',
    validUntil: raw.validUntil ?? raw.valid_until ?? '',
    gateId: raw.gateId ?? raw.gate_id ?? '',
    gateName:
      raw.gateName ?? raw.gate_name ?? ((raw.gateId ?? raw.gate_id) ? 'Assigned gate' : 'Any gate'),
    notes: raw.notes ?? null,
    vehiclePlate: raw.vehiclePlate ?? raw.vehicle_plate ?? null,
    vehicleMake: raw.vehicleMake ?? raw.vehicle_make ?? null,
    vehicleColour: raw.vehicleColour ?? raw.vehicle_colour ?? null,
    qrTokenRef: raw.qrTokenRef ?? raw.qr_token ?? null,
    pin: raw.pin ?? null,
    partySize: raw.partySize ?? raw.party_size ?? 1,
    rsvpStatus:
      raw.rsvpStatus ??
      (status === 'declined' ? 'declined' : raw.visitor_confirmed ? 'confirmed' : 'not_responded'),
    createdAt: raw.createdAt ?? raw.created_at ?? '',
  };
}

// ─── Auth ─────────────────────────────────────────────────────────────────────

export interface LoginSuccessResponse {
  mfaPending: false;
  userId: string;
  email: string;
  fullName: string;
  platformRole: string;
  propertyRoles: Array<{ propertyId: string; role: string; subdomain?: string | null }>;
}

export interface LoginMfaPendingResponse {
  mfaPending: true;
}

export interface LoginMfaSetupRequiredResponse {
  mfaSetupRequired: true;
}

export type LoginResponse =
  | LoginSuccessResponse
  | LoginMfaPendingResponse
  | LoginMfaSetupRequiredResponse;

/**
 * POST /api/v1/auth/login
 * Returns 200 (full session cookies), 202 (mfa_pending cookie), or 403
 * (mfaSetupRequired — session cookies issued so the client can call
 * /me/mfa/setup to enrol).
 * ADR-001: No token handled client-side.
 */
export async function login(email: string, password: string): Promise<LoginResponse> {
  const response = await apiClient.post<LoginResponse & { mfaSetupRequired?: boolean }>(
    '/auth/login',
    { email, password },
    { validateStatus: (s) => (s >= 200 && s < 300) || s === 403 },
  );
  if (response.status === 202) return { mfaPending: true };
  if (response.status === 403 && response.data?.mfaSetupRequired) {
    return { mfaSetupRequired: true };
  }
  return response.data as LoginSuccessResponse;
}

export interface AuthUser {
  userId: string;
  email: string;
  fullName: string;
  platformRole: string;
  propertyRoles: Array<{ propertyId: string; role: string; subdomain?: string | null }>;
}

/** POST /api/v1/auth/mfa/verify — exchanges mfa_pending cookie for full session. */
export async function verifyMfa(code: string): Promise<AuthUser> {
  const response = await apiClient.post<AuthUser>('/auth/mfa/verify', { code });
  return response.data;
}

/** POST /api/v1/auth/password/reset-request — always 204 (anti-enumeration). */
export async function requestPasswordReset(email: string): Promise<void> {
  await apiClient.post('/auth/password/reset-request', { email });
}

/**
 * Consent payload captured at the activation step. Required when the
 * password-reset endpoint is acting as the activation flow; the in-account
 * password-change flow does not use this argument.
 *
 * KDPA §32 + §33: privacy + terms + age-of-majority consents are persisted
 * atomically with the password update on the server.
 */
export interface ActivationConsentPayload {
  privacyPolicyAccepted: boolean;
  privacyPolicyVersion: string;
  termsAccepted: boolean;
  termsVersion: string;
  ageEighteenPlus: boolean;
  ageAttestationVersion: string;
}

/** POST /api/v1/auth/password/reset — completes reset with single-use token. */
export async function resetPassword(
  token: string,
  newPassword: string,
  consent?: ActivationConsentPayload,
): Promise<void> {
  await apiClient.post('/auth/password/reset', {
    token,
    newPassword,
    ...(consent ? { consent } : {}),
  });
}

/** POST /api/v1/auth/logout — clears session cookies, returns 204. */
export async function logout(): Promise<void> {
  await apiClient.post('/auth/logout');
}

// ─── Me / Profile ─────────────────────────────────────────────────────────────

/** GET /api/v1/me */
export async function getMe(): Promise<MeProfile> {
  const r = await apiClient.get<MeProfile>('/me');
  return r.data;
}

/** PATCH /api/v1/me */
export async function updateMe(body: { fullName?: string; phone?: string }): Promise<MeProfile> {
  const r = await apiClient.patch<MeProfile>('/me', body);
  return r.data;
}

/** POST /api/v1/auth/password/change */
export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await apiClient.post('/auth/password/change', { currentPassword, newPassword });
}

// ─── MFA Self-Service ────────────────────────────────────────────────────────

export interface MfaSetupResult {
  /** Base32 TOTP secret — shown once, for manual entry into the authenticator app. */
  secret: string;
  /** otpauth:// URI the client renders as a QR code. */
  qrUri: string;
}

/** POST /api/v1/me/mfa/setup — initiates enrolment, returns {secret, qrUri}. */
export async function setupMfa(): Promise<MfaSetupResult> {
  const r = await apiClient.post<MfaSetupResult>('/me/mfa/setup');
  return r.data;
}

/** POST /api/v1/me/mfa/confirm — confirms enrolment with first valid 6-digit code. */
export async function confirmMfaSetup(code: string): Promise<void> {
  await apiClient.post('/me/mfa/confirm', { code });
}

/** DELETE /api/v1/me/mfa — disables MFA on the account (re-verifies password). */
export async function disableMfa(currentPassword: string): Promise<void> {
  await apiClient.delete('/me/mfa', { data: { currentPassword } });
}

// ─── Sessions ────────────────────────────────────────────────────────────────

/** GET /api/v1/me/sessions */
export async function getMySessions(): Promise<DeviceSession[]> {
  const r = await apiClient.get<DeviceSession[]>('/me/sessions');
  return r.data;
}

/** DELETE /api/v1/me/sessions/:sessionId */
export async function revokeSession(sessionId: string): Promise<void> {
  await apiClient.delete(`/me/sessions/${sessionId}`);
}

/** Registered FCM/APNs push device token. */
export interface PushDeviceToken {
  id: string;
  platform: 'fcm' | 'apns';
  createdAt?: string;
}

/** GET /api/v1/me/device-tokens — list the user's active push tokens. */
export async function getDeviceTokens(): Promise<PushDeviceToken[]> {
  const r = await apiClient.get<PushDeviceToken[]>('/me/device-tokens');
  return r.data;
}

/**
 * POST /api/v1/me/device-tokens — register an FCM web push token.
 * Web push delivers via FCM, so the platform is 'fcm'.
 */
export async function registerDeviceToken(token: string): Promise<{ id: string }> {
  const r = await apiClient.post<{ id: string }>('/me/device-tokens', {
    token,
    platform: 'fcm',
  });
  return r.data;
}

/** DELETE /api/v1/me/device-tokens/:tokenId — unregister a push token. */
export async function deleteDeviceToken(tokenId: string): Promise<void> {
  await apiClient.delete(`/me/device-tokens/${tokenId}`);
}

// ─── Notification Preferences ────────────────────────────────────────────────

/** GET /api/v1/me/notification-preferences — propertyId required by the server. */
export async function getNotificationPreferences(
  propertyId: string,
): Promise<NotificationPreferences> {
  const r = await apiClient.get<NotificationPreferences>('/me/notification-preferences', {
    params: { propertyId },
  });
  return r.data;
}

/** PATCH /api/v1/me/notification-preferences — propertyId required by the server. */
export async function updateNotificationPreferences(
  propertyId: string,
  body: Partial<NotificationPreferences>,
): Promise<NotificationPreferences> {
  const r = await apiClient.patch<NotificationPreferences>('/me/notification-preferences', body, {
    params: { propertyId },
  });
  return r.data;
}

// ─── Favourites ──────────────────────────────────────────────────────────────

/** GET /api/v1/me/favourites */
export async function getFavourites(propertyId: string, unitId: string): Promise<Favourite[]> {
  const r = await apiClient.get<Favourite[]>('/me/favourites', {
    params: { propertyId, unitId },
  });
  return r.data;
}

/** POST /api/v1/me/favourites */
export async function addFavourite(body: {
  propertyId: string;
  unitId: string;
  visitorName: string;
  visitorPhone: string;
}): Promise<Favourite> {
  const r = await apiClient.post<Favourite>('/me/favourites', body);
  return r.data;
}

/** PATCH /api/v1/me/favourites/:id */
export async function updateFavourite(id: string, body: Partial<Favourite>): Promise<Favourite> {
  const r = await apiClient.patch<Favourite>(`/me/favourites/${id}`, body);
  return r.data;
}

/** DELETE /api/v1/me/favourites/:id */
export async function deleteFavourite(id: string): Promise<void> {
  await apiClient.delete(`/me/favourites/${id}`);
}

// ─── Gates ───────────────────────────────────────────────────────────────────

/** GET /api/v1/properties/:propertyId/gates */
export async function getGates(propertyId: string): Promise<Gate[]> {
  const r = await apiClient.get<Gate[]>(`/properties/${propertyId}/gates`);
  return r.data;
}

// ─── Invitations ─────────────────────────────────────────────────────────────

export interface ListInvitationsParams {
  status?: string;
  /** Comma-separated list of statuses — overrides `status` when provided. */
  statusIn?: string;
  dateFrom?: string;
  dateTo?: string;
  visitorName?: string;
  page?: number;
  perPage?: number;
  limit?: number;
}

/** GET /api/v1/properties/:propertyId/invitations */
export async function getInvitations(
  propertyId: string,
  params?: ListInvitationsParams,
): Promise<PaginatedResponse<Invitation>> {
  const { limit, ...rest } = params ?? {};
  const queryParams = {
    ...rest,
    ...(params?.perPage !== undefined
      ? { perPage: params.perPage }
      : limit !== undefined
        ? { perPage: limit }
        : {}),
  };

  const r = await apiClient.get<PaginatedResponse<InvitationListItem> | InvitationListItem[]>(
    `/properties/${propertyId}/invitations`,
    { params: queryParams },
  );

  if (Array.isArray(r.data)) {
    return {
      data: r.data.map((inv) => normalizeInvitation(inv)),
      meta: {
        total: r.data.length,
        page: params?.page ?? 1,
        perPage: queryParams.perPage ?? r.data.length,
        totalPages: 1,
      },
    };
  }

  return {
    ...r.data,
    data: r.data.data.map((inv) => normalizeInvitation(inv)),
  };
}

/** GET /api/v1/properties/:propertyId/invitations/:id */
export async function getInvitation(propertyId: string, id: string): Promise<Invitation> {
  const r = await apiClient.get<Invitation>(`/properties/${propertyId}/invitations/${id}`);
  return r.data;
}

/** POST /api/v1/properties/:propertyId/invitations */
export async function createInvitation(
  propertyId: string,
  body: CreateInvitationBody,
): Promise<Invitation> {
  const r = await apiClient.post<Invitation>(`/properties/${propertyId}/invitations`, body);
  return r.data;
}

/** POST /api/v1/properties/:propertyId/invitations/bulk — create many invitations for an event. */
export async function createBulkInvitation(
  propertyId: string,
  body: CreateBulkInvitationBody,
): Promise<CreateBulkInvitationResult> {
  const r = await apiClient.post<CreateBulkInvitationResult>(
    `/properties/${propertyId}/invitations/bulk`,
    body,
  );
  return r.data;
}

/**
 * POST /api/v1/properties/:propertyId/invitations/registration-links
 * Creates a public event registration link — residents share the returned URL.
 */
export async function createRegistrationLink(
  propertyId: string,
  body: CreateRegistrationLinkBody,
): Promise<RegistrationLinkResult> {
  const r = await apiClient.post<RegistrationLinkResult>(
    `/properties/${propertyId}/invitations/registration-links`,
    body,
  );
  return r.data;
}

/** POST /api/v1/properties/:propertyId/invitations/preview */
export async function previewInvitation(
  propertyId: string,
  body: CreateInvitationBody,
): Promise<InvitationPreview> {
  const r = await apiClient.post<InvitationPreview>(
    `/properties/${propertyId}/invitations/preview`,
    body,
  );
  return r.data;
}

/** DELETE /api/v1/properties/:propertyId/invitations/:id */
export async function cancelInvitation(propertyId: string, id: string): Promise<void> {
  await apiClient.delete(`/properties/${propertyId}/invitations/${id}`);
}

// ─── Access Logs (Visitor History) ───────────────────────────────────────────

export interface ListAccessLogsParams {
  visitorName?: string;
  dateFrom?: string;
  dateTo?: string;
  invitationType?: string;
  cursor?: string;
  limit?: number;
}

/** GET /api/v1/properties/:propertyId/units/:unitId/access-logs */
export async function getUnitAccessLogs(
  propertyId: string,
  unitId: string,
  params?: ListAccessLogsParams,
): Promise<CursorPaginatedResponse<AccessLogEntry>> {
  const r = await apiClient.get<CursorPaginatedResponse<AccessLogEntry>>(
    `/properties/${propertyId}/units/${unitId}/access-logs`,
    { params },
  );
  return r.data;
}

// ─── Recurring Passes ────────────────────────────────────────────────────────

/** GET /api/v1/properties/:propertyId/recurring-passes */
export async function getRecurringPasses(propertyId: string): Promise<RecurringPass[]> {
  const r = await apiClient.get<ListEnvelope<RecurringPassListItem>>(
    `/properties/${propertyId}/recurring-passes`,
  );
  return unwrapListEnvelope(r.data).map((item) => normalizeRecurringPass(item, 'recurring'));
}

/** POST /api/v1/properties/:propertyId/recurring-passes */
export async function createRecurringPass(
  propertyId: string,
  body: CreateRecurringPassBody,
): Promise<RecurringPass> {
  const payload = {
    visitorName: body.visitorName,
    visitorPhone: body.visitorPhone,
    passType: 'recurring' as const,
    rrule: recurrencePatternToRRule(body.recurrencePattern, body.scheduleDays),
    windowStartTime: body.entryTimeStart,
    windowEndTime: body.entryTimeEnd,
    validFrom: body.validFrom,
    ...(body.validUntil ? { validUntil: body.validUntil } : {}),
    ...(body.gateId ? { gateId: body.gateId } : {}),
    ...(body.notes ? { note: body.notes } : {}),
  };

  const r = await apiClient.post<
    Partial<RecurringPass> & {
      id: string;
      status: RecurringPass['status'];
      validFrom: string;
      validUntil: string | null;
      createdAt: string;
    }
  >(`/properties/${propertyId}/recurring-passes`, payload);
  return buildCreatedRecurringPass(r.data, body, 'recurring');
}

/** POST /api/v1/properties/:propertyId/recurring-passes/:id/suspend */
export async function suspendRecurringPass(propertyId: string, id: string): Promise<void> {
  await apiClient.post(`/properties/${propertyId}/recurring-passes/${id}/suspend`);
}

/** POST /api/v1/properties/:propertyId/recurring-passes/:id/reinstate */
export async function reinstateRecurringPass(propertyId: string, id: string): Promise<void> {
  await apiClient.post(`/properties/${propertyId}/recurring-passes/${id}/reinstate`);
}

/** DELETE /api/v1/properties/:propertyId/recurring-passes/:id */
export async function revokeRecurringPass(propertyId: string, id: string): Promise<void> {
  await apiClient.delete(`/properties/${propertyId}/recurring-passes/${id}`);
}

/**
 * PATCH /api/v1/properties/:propertyId/recurring-passes/:id
 * Renew extends validity by setting a new end date. Only validUntil is sent so the
 * schedule (and therefore the QR token / PIN) is left intact.
 */
export async function renewRecurringPass(
  propertyId: string,
  id: string,
  validUntil: string,
): Promise<void> {
  await apiClient.patch(`/properties/${propertyId}/recurring-passes/${id}`, { validUntil });
}

// ─── Domestic Workers ────────────────────────────────────────────────────────

/** GET /api/v1/properties/:propertyId/domestic-workers */
export async function getDomesticWorkers(propertyId: string): Promise<RecurringPass[]> {
  const r = await apiClient.get<ListEnvelope<RecurringPassListItem>>(
    `/properties/${propertyId}/domestic-workers`,
  );
  return unwrapListEnvelope(r.data).map((item) => normalizeRecurringPass(item, 'domestic_worker'));
}

/** POST /api/v1/properties/:propertyId/domestic-workers */
export async function createDomesticWorker(
  propertyId: string,
  body: CreateRecurringPassBody,
): Promise<RecurringPass> {
  const payload = {
    workerName: body.visitorName,
    workerPhone: body.visitorPhone,
    workDays: recurrencePatternToWorkDays(body.recurrencePattern, body.scheduleDays),
    windowStartTime: body.entryTimeStart,
    windowEndTime: body.entryTimeEnd,
    validFrom: body.validFrom,
    ...(body.validUntil ? { validUntil: body.validUntil } : {}),
    ...(body.gateId ? { gateId: body.gateId } : {}),
    ...(body.idNumber ? { idNumber: body.idNumber } : {}),
  };

  const r = await apiClient.post<
    Partial<RecurringPass> & {
      id: string;
      status: RecurringPass['status'];
      validFrom: string;
      validUntil: string | null;
      createdAt: string;
    }
  >(`/properties/${propertyId}/domestic-workers`, payload);
  return buildCreatedRecurringPass(r.data, body, 'domestic_worker');
}

/** POST /api/v1/properties/:propertyId/domestic-workers/:id/suspend */
export async function suspendDomesticWorker(propertyId: string, id: string): Promise<void> {
  await apiClient.post(`/properties/${propertyId}/domestic-workers/${id}/suspend`);
}

/** DELETE /api/v1/properties/:propertyId/domestic-workers/:id */
export async function revokeDomesticWorker(propertyId: string, id: string): Promise<void> {
  await apiClient.delete(`/properties/${propertyId}/domestic-workers/${id}`);
}

/**
 * PATCH /api/v1/properties/:propertyId/domestic-workers/:id
 * Renew extends validity by setting a new end date only.
 */
export async function renewDomesticWorker(
  propertyId: string,
  id: string,
  validUntil: string,
): Promise<void> {
  await apiClient.patch(`/properties/${propertyId}/domestic-workers/${id}`, { validUntil });
}

// ─── Delivery Passes ─────────────────────────────────────────────────────────

/** GET /api/v1/properties/:propertyId/delivery-passes */
export async function getDeliveryPasses(
  propertyId: string,
  params?: { status?: string },
): Promise<DeliveryPass[]> {
  const r = await apiClient.get<ListEnvelope<DeliveryPass>>(
    `/properties/${propertyId}/delivery-passes`,
    {
      params,
    },
  );
  return unwrapListEnvelope(r.data);
}

/** POST /api/v1/properties/:propertyId/delivery-passes */
export async function createDeliveryPass(
  propertyId: string,
  body: CreateDeliveryPassBody,
): Promise<DeliveryPassWithPin> {
  const r = await apiClient.post<DeliveryPassWithPin>(`/properties/${propertyId}/delivery-passes`, {
    validUntil: body.validUntil,
  });
  return r.data;
}

/** DELETE /api/v1/properties/:propertyId/delivery-passes/:id */
export async function cancelDeliveryPass(propertyId: string, id: string): Promise<void> {
  await apiClient.delete(`/properties/${propertyId}/delivery-passes/${id}`);
}

// ─── Taxi Passes ─────────────────────────────────────────────────────────────

/** GET /api/v1/properties/:propertyId/taxi-passes */
export async function getTaxiPasses(propertyId: string): Promise<TaxiPass[]> {
  const r = await apiClient.get<ListEnvelope<TaxiPass>>(`/properties/${propertyId}/taxi-passes`);
  return unwrapListEnvelope(r.data);
}

/** POST /api/v1/properties/:propertyId/taxi-passes */
export async function createTaxiPass(
  propertyId: string,
  body: CreateTaxiPassBody,
): Promise<TaxiPassWithPin> {
  const payload = {
    ...(body.vehiclePlate ? { vehiclePlate: body.vehiclePlate } : {}),
    ...(body.validityMinutes !== undefined ? { validityWindowMinutes: body.validityMinutes } : {}),
  };

  const r = await apiClient.post<TaxiPassWithPin>(`/properties/${propertyId}/taxi-passes`, payload);
  return r.data;
}

/** DELETE /api/v1/properties/:propertyId/taxi-passes/:id */
export async function cancelTaxiPass(propertyId: string, id: string): Promise<void> {
  await apiClient.delete(`/properties/${propertyId}/taxi-passes/${id}`);
}

// ─── Walk-In Requests ────────────────────────────────────────────────────────

/** GET /api/v1/walk-in-requests/:requestId */
export async function getWalkInRequest(requestId: string): Promise<WalkInRequest> {
  const r = await apiClient.get<WalkInRequest>(`/walk-in-requests/${requestId}`);
  return r.data;
}

/**
 * GET /api/v1/walk-in-requests
 * The resident's currently-pending walk-in requests, sourced directly from the
 * request records (not the notification feed) so the dashboard banner reflects
 * the true pending state regardless of notification read-state.
 */
export async function getResidentPendingWalkIns(): Promise<WalkInRequest[]> {
  const r = await apiClient.get<WalkInRequest[]>('/walk-in-requests');
  return r.data;
}

/** POST /api/v1/walk-in-requests/:requestId/approve */
export async function approveWalkIn(requestId: string): Promise<void> {
  await apiClient.post(`/walk-in-requests/${requestId}/approve`);
}

/** POST /api/v1/walk-in-requests/:requestId/deny */
export async function denyWalkIn(requestId: string, reason?: string): Promise<void> {
  await apiClient.post(`/walk-in-requests/${requestId}/deny`, { reason });
}

/** POST /api/v1/walk-in-requests/:requestId/cancel — cancel a walk-in request */
export async function cancelWalkIn(requestId: string): Promise<void> {
  await apiClient.post(`/walk-in-requests/${requestId}/cancel`);
}

// ─── Notifications ───────────────────────────────────────────────────────────

export interface ListNotificationsParams {
  category?: string;
  isRead?: boolean;
  page?: number;
  pageSize?: number;
}

/** GET /api/v1/notifications */
export async function getNotifications(
  params?: ListNotificationsParams,
): Promise<PaginatedResponse<AppNotification>> {
  const r = await apiClient.get<PaginatedResponse<AppNotification>>('/notifications', { params });
  return r.data;
}

/** POST /api/v1/notifications/mark-all-read */
export async function markAllNotificationsRead(): Promise<void> {
  await apiClient.post('/notifications/mark-all-read');
}

/** DELETE /api/v1/notifications/:id */
export async function deleteNotification(id: string): Promise<void> {
  await apiClient.delete(`/notifications/${id}`);
}

/** DELETE /api/v1/notifications/read — delete all read notifications */
export async function deleteAllReadNotifications(): Promise<{ deleted: number }> {
  const r = await apiClient.delete<{ deleted: number }>('/notifications/read');
  return r.data;
}

// ─── DSAR ────────────────────────────────────────────────────────────────────

/** POST /api/v1/me/dsar */
export async function submitDsar(body: {
  requestType: DsarType;
  additionalDetails?: string;
}): Promise<DsarRequest> {
  const r = await apiClient.post<DsarRequest>('/me/dsar', body);
  return r.data;
}

/** GET /api/v1/me/dsar */
export async function getDsarRequests(): Promise<DsarRequest[]> {
  const r = await apiClient.get<DsarRequest[]>('/me/dsar');
  return r.data;
}

// ─── Consent ─────────────────────────────────────────────────────────────────

export interface ConsentEvent {
  id: string;
  consent_type: string;
  version: string;
  granted: boolean;
  created_at: string;
}

/** POST /api/v1/me/consent — record or withdraw a consent for a scope */
export async function recordConsent(body: {
  scope: string;
  version: string;
  granted: boolean;
  propertyId?: string;
}): Promise<ConsentEvent> {
  const r = await apiClient.post<ConsentEvent>('/me/consent', body);
  return r.data;
}

/** GET /api/v1/me/consent — list this user's consent history (optionally per scope) */
export async function getConsentHistory(scope?: string): Promise<ConsentEvent[]> {
  const r = await apiClient.get<ConsentEvent[]>('/me/consent', {
    params: scope ? { scope } : undefined,
  });
  return r.data;
}

// ─── Enterprise Tenant ────────────────────────────────────────────────────────

/** GET /api/v1/properties/:propertyId/enterprise-tenants */
export async function listEnterpriseTenants(propertyId: string): Promise<EnterpriseTenant[]> {
  const r = await apiClient.get<EnterpriseTenant[]>(`/properties/${propertyId}/enterprise-tenants`);
  return r.data;
}

export interface CreateEnterpriseTenantBody {
  companyName: string;
  unitId?: string;
  unitReference?: string;
  floor?: string;
  adminFullName: string;
  adminEmail: string;
  adminPhone?: string;
}

export interface CreateEnterpriseTenantResult {
  tenantId: string;
  companyName: string;
  adminUserId: string;
  adminEmail: string;
}

/** POST /api/v1/properties/:propertyId/enterprise-tenants */
export async function createEnterpriseTenant(
  propertyId: string,
  body: CreateEnterpriseTenantBody,
): Promise<CreateEnterpriseTenantResult> {
  const r = await apiClient.post<CreateEnterpriseTenantResult>(
    `/properties/${propertyId}/enterprise-tenants`,
    body,
  );
  return r.data;
}

/** GET /api/v1/properties/:propertyId/enterprise-tenants/:tenantId */
export async function getEnterpriseTenant(
  propertyId: string,
  tenantId: string,
): Promise<EnterpriseTenant> {
  const r = await apiClient.get<EnterpriseTenant>(
    `/properties/${propertyId}/enterprise-tenants/${tenantId}`,
  );
  return r.data;
}

/** PATCH /api/v1/properties/:propertyId/enterprise-tenants/:tenantId */
export async function updateEnterpriseTenant(
  propertyId: string,
  tenantId: string,
  body: { companyName?: string; receptionHours?: string },
): Promise<{ id: string; companyName: string | null; receptionHours: string | null }> {
  const r = await apiClient.patch<{
    id: string;
    companyName: string | null;
    receptionHours: string | null;
  }>(`/properties/${propertyId}/enterprise-tenants/${tenantId}`, body);
  return r.data;
}

/** GET /api/v1/properties/:propertyId/enterprise-tenants/:tenantId/employees */
export async function getEnterpriseEmployees(
  propertyId: string,
  tenantId: string,
): Promise<EnterpriseEmployee[]> {
  const r = await apiClient.get<EnterpriseEmployee[]>(
    `/properties/${propertyId}/enterprise-tenants/${tenantId}/employees`,
  );
  return r.data;
}

/** POST /api/v1/properties/:propertyId/enterprise-tenants/:tenantId/employees */
export async function addEnterpriseEmployee(
  propertyId: string,
  tenantId: string,
  body: { email: string; fullName: string; phone?: string },
): Promise<EnterpriseEmployee> {
  const r = await apiClient.post<EnterpriseEmployee>(
    `/properties/${propertyId}/enterprise-tenants/${tenantId}/employees`,
    body,
  );
  return r.data;
}

/** POST /api/v1/properties/:propertyId/enterprise-tenants/:tenantId/employees/:empId/deactivate */
export async function deactivateEnterpriseEmployee(
  propertyId: string,
  tenantId: string,
  empId: string,
): Promise<{ status: string; cancelledInvitations: number }> {
  const r = await apiClient.post<{ status: string; cancelledInvitations: number }>(
    `/properties/${propertyId}/enterprise-tenants/${tenantId}/employees/${empId}/deactivate`,
  );
  return r.data;
}

/** POST /api/v1/properties/:propertyId/enterprise-tenants/:tenantId/employees/:empId/reactivate */
export async function reactivateEnterpriseEmployee(
  propertyId: string,
  tenantId: string,
  empId: string,
): Promise<{ status: string }> {
  const r = await apiClient.post<{ status: string }>(
    `/properties/${propertyId}/enterprise-tenants/${tenantId}/employees/${empId}/reactivate`,
  );
  return r.data;
}

/** GET /api/v1/properties/:propertyId/enterprise-tenants/:tenantId/worker-passes */
export async function getEnterpriseWorkerPasses(
  propertyId: string,
  tenantId: string,
): Promise<WorkerPass[]> {
  const r = await apiClient.get<WorkerPass[]>(
    `/properties/${propertyId}/enterprise-tenants/${tenantId}/worker-passes`,
  );
  return r.data;
}

/** POST /api/v1/properties/:propertyId/enterprise-tenants/:tenantId/worker-passes */
export async function createEnterpriseWorkerPass(
  propertyId: string,
  tenantId: string,
  body: CreateWorkerPassBody,
): Promise<WorkerPass> {
  const r = await apiClient.post<WorkerPass>(
    `/properties/${propertyId}/enterprise-tenants/${tenantId}/worker-passes`,
    body,
  );
  return r.data;
}

/** POST /api/v1/properties/:propertyId/enterprise-tenants/:tenantId/worker-passes/:passId/suspend */
export async function suspendEnterpriseWorkerPass(
  propertyId: string,
  tenantId: string,
  passId: string,
): Promise<void> {
  await apiClient.post(
    `/properties/${propertyId}/enterprise-tenants/${tenantId}/worker-passes/${passId}/suspend`,
  );
}

/** DELETE /api/v1/properties/:propertyId/enterprise-tenants/:tenantId/worker-passes/:passId */
export async function revokeEnterpriseWorkerPass(
  propertyId: string,
  tenantId: string,
  passId: string,
): Promise<void> {
  await apiClient.delete(
    `/properties/${propertyId}/enterprise-tenants/${tenantId}/worker-passes/${passId}`,
  );
}

// ─── Enterprise Reception (ADR 008) ──────────────────────────────────────────

export type EnterpriseSettings = {
  notifyAdminOnArrival: boolean;
  notifyAdminOnWalkin: boolean;
  requireAdminApprovalForEmployeeInvites: boolean;
};

export type EnterpriseApprovalQueueItem = {
  id: string;
  visitorName: string;
  vehiclePlate: string | null;
  hostType: 'enterprise_employee' | 'enterprise_unit';
  hostEmployeeId: string | null;
  hostEmployeeName: string | null;
  requestedAt: string;
  autoTimeoutAt: string;
};

export type EnterpriseExpectedItem = {
  id: string;
  kind: 'invitation' | 'walk_in';
  visitorName: string;
  expectedAt: string;
  validUntil: string | null;
  status: string;
  hostName: string | null;
};

export type EnterpriseOnPremisesItem = {
  id: string;
  visitorName: string;
  vehiclePlate: string | null;
  checkedInAt: string;
  sourceType: string;
  hostName: string | null;
};

export async function getEnterpriseSettings(
  propertyId: string,
  tenantId: string,
): Promise<EnterpriseSettings> {
  const r = await apiClient.get<EnterpriseSettings>(
    `/properties/${propertyId}/enterprise-tenants/${tenantId}/settings`,
  );
  return r.data;
}

export async function updateEnterpriseSettings(
  propertyId: string,
  tenantId: string,
  body: Partial<EnterpriseSettings>,
): Promise<EnterpriseSettings> {
  const r = await apiClient.patch<EnterpriseSettings>(
    `/properties/${propertyId}/enterprise-tenants/${tenantId}/settings`,
    body,
  );
  return r.data;
}

export async function getEnterpriseApprovalQueue(
  propertyId: string,
  tenantId: string,
): Promise<EnterpriseApprovalQueueItem[]> {
  const r = await apiClient.get<EnterpriseApprovalQueueItem[]>(
    `/properties/${propertyId}/enterprise-tenants/${tenantId}/approval-queue`,
  );
  return r.data;
}

export async function getEnterpriseExpectedToday(
  propertyId: string,
  tenantId: string,
): Promise<EnterpriseExpectedItem[]> {
  const r = await apiClient.get<EnterpriseExpectedItem[]>(
    `/properties/${propertyId}/enterprise-tenants/${tenantId}/expected-today`,
  );
  return r.data;
}

export async function getEnterpriseOnPremises(
  propertyId: string,
  tenantId: string,
): Promise<EnterpriseOnPremisesItem[]> {
  const r = await apiClient.get<EnterpriseOnPremisesItem[]>(
    `/properties/${propertyId}/enterprise-tenants/${tenantId}/on-premises`,
  );
  return r.data;
}

// ─── Property Manager — Stats ────────────────────────────────────────────────

/** GET /api/v1/properties/:propertyId/stats */
export async function getPropertyStats(propertyId: string): Promise<PropertyStats> {
  const r = await apiClient.get<PropertyStats>(`/properties/${propertyId}/stats`);
  return r.data;
}

// ─── Property Manager — Access Logs ──────────────────────────────────────────

/** GET /api/v1/properties/:propertyId/access-logs (PM view — full detail) */
export async function getPmAccessLogs(
  propertyId: string,
  filters?: PmAccessLogFilters,
): Promise<CursorPaginatedResponse<PmAccessLogEntry>> {
  // Map client filter shape to server DTO shape
  const params: Record<string, unknown> = {};
  if (filters?.cursor) params.cursor = filters.cursor;
  if (filters?.limit) params.limit = filters.limit;
  if (filters?.gateIds?.length) params.gateId = filters.gateIds[0];
  if (filters?.dateFrom) params.dateFrom = filters.dateFrom;
  if (filters?.dateTo) params.dateTo = filters.dateTo;
  if (filters?.visitorName) params.visitorName = filters.visitorName;
  if (filters?.unit) params.unitId = filters.unit;
  if (filters?.guardName) params.guardName = filters.guardName;
  if (filters?.checkedInOnly) params.checkedInOnly = true;

  const r = await apiClient.get<CursorPaginatedResponse<PmAccessLogEntry>>(
    `/properties/${propertyId}/access-logs`,
    { params },
  );
  return r.data;
}

/** GET /api/v1/properties/:propertyId/access-logs/live — currently checked-in */
export async function getPmAccessLogLive(propertyId: string): Promise<PmAccessLogEntry[]> {
  const r = await apiClient.get<PmAccessLogEntry[]>(`/properties/${propertyId}/access-logs/live`);
  return r.data;
}

/** POST /api/v1/properties/:propertyId/access-logs/export */
export async function exportAccessLog(
  propertyId: string,
  body: {
    format: 'csv' | 'pdf';
    dateFrom: string;
    dateTo: string;
    gateIds?: string[];
    statuses?: string[];
    invitationTypes?: string[];
    visitorName?: string;
    unit?: string;
    guardName?: string;
  },
): Promise<AccessLogExportJob> {
  const r = await apiClient.post<AccessLogExportJob>(
    `/properties/${propertyId}/access-logs/export`,
    body,
  );
  return r.data;
}

/** POST /api/v1/properties/:propertyId/access-logs/:visitId/reopen */
export async function reopenVisit(propertyId: string, visitId: string): Promise<void> {
  await apiClient.post(`/properties/${propertyId}/access-logs/${visitId}/reopen`);
}

// ─── Property Manager — Incidents (summary for dashboard) ────────────────────

/** GET /api/v1/properties/:propertyId/incidents */
export async function getPmIncidents(
  propertyId: string,
  params?: { status?: IncidentStatus; limit?: number; page?: number; perPage?: number },
): Promise<PaginatedResponse<IncidentSummary>> {
  const { limit, ...rest } = params ?? {};
  const queryParams = {
    ...rest,
    ...(rest.perPage !== undefined ? {} : limit !== undefined ? { perPage: limit } : {}),
  };
  const r = await apiClient.get<PaginatedResponse<IncidentSummary>>(
    `/properties/${propertyId}/incidents`,
    { params: queryParams },
  );
  return r.data;
}

// ─── Property Manager — Walk-Ins Pending ─────────────────────────────────────

/** GET /api/v1/properties/:propertyId/walk-ins?status=pending */
export async function getPmPendingWalkIns(propertyId: string): Promise<PmWalkInRequest[]> {
  const r = await apiClient.get<PmWalkInRequest[]>(`/properties/${propertyId}/walk-ins`, {
    params: { status: 'pending' },
  });
  return r.data;
}

/** GET upcoming visitors in next 2 hours via the invitations endpoint. */
export async function getPmUpcomingVisitors(propertyId: string): Promise<Invitation[]> {
  const now = new Date();
  const twoHoursLater = new Date(now.getTime() + 2 * 60 * 60 * 1000);
  const r = await getInvitations(propertyId, {
    status: 'active',
    dateFrom: now.toISOString(),
    dateTo: twoHoursLater.toISOString(),
    perPage: 5,
  });
  return r.data;
}

// ─── Property Manager — Incidents (full CRUD) ────────────────────────────────

/** GET /api/v1/properties/:propertyId/incidents/:id */
export async function getPmIncidentDetail(
  propertyId: string,
  incidentId: string,
): Promise<IncidentDetail> {
  const r = await apiClient.get<IncidentDetail>(
    `/properties/${propertyId}/incidents/${incidentId}`,
  );
  return r.data;
}

/** POST /api/v1/properties/:propertyId/incidents/:id/assign */
export async function assignIncident(
  propertyId: string,
  incidentId: string,
  assignedTo: string,
): Promise<void> {
  await apiClient.post(`/properties/${propertyId}/incidents/${incidentId}/assign`, {
    assignedTo,
  });
}

/** POST /api/v1/properties/:propertyId/incidents/:id/notes */
export async function addIncidentNote(
  propertyId: string,
  incidentId: string,
  body: string,
): Promise<void> {
  await apiClient.post(`/properties/${propertyId}/incidents/${incidentId}/notes`, {
    content: body,
  });
}

/** POST /api/v1/properties/:propertyId/incidents/:id/close */
export async function closeIncident(
  propertyId: string,
  incidentId: string,
  resolutionNote: string,
): Promise<void> {
  await apiClient.post(`/properties/${propertyId}/incidents/${incidentId}/close`, {
    resolutionNote,
  });
}

/** POST /api/v1/properties/:propertyId/incidents/:id/reopen */
export async function reopenIncident(
  propertyId: string,
  incidentId: string,
  reason: string,
): Promise<void> {
  await apiClient.post(`/properties/${propertyId}/incidents/${incidentId}/reopen`, {
    reason,
  });
}

// ─── Property Manager — Users ─────────────────────────────────────────────────

/** GET /api/v1/properties/:propertyId/users */
export async function getPmUsers(
  propertyId: string,
  params?: {
    role?: UserRole;
    status?: PmUserStatus;
    page?: number;
    limit?: number;
    perPage?: number;
    search?: string;
  },
): Promise<PaginatedResponse<PmUser>> {
  const r = await apiClient.get<PaginatedResponse<PmUser>>(`/properties/${propertyId}/users`, {
    params,
  });
  return r.data;
}

/** POST /api/v1/properties/:propertyId/users/residents */
export async function inviteResident(propertyId: string, body: InviteResidentBody): Promise<void> {
  await apiClient.post(`/properties/${propertyId}/users/residents`, body);
}

/** POST /api/v1/properties/:propertyId/users/guards */
export async function inviteGuard(propertyId: string, body: InviteGuardBody): Promise<void> {
  await apiClient.post(`/properties/${propertyId}/users/guards`, body);
}

/** POST /api/v1/properties/:propertyId/users/supervisors */
export async function inviteSupervisor(
  propertyId: string,
  body: InviteSupervisorBody,
): Promise<void> {
  await apiClient.post(`/properties/${propertyId}/users/supervisors`, body);
}

/** POST /api/v1/properties/:propertyId/users/managers */
export async function invitePropertyManager(
  propertyId: string,
  body: InviteSupervisorBody,
): Promise<void> {
  await apiClient.post(`/properties/${propertyId}/users/managers`, body);
}

/** POST /api/v1/properties/:propertyId/users/:userId/suspend */
export async function suspendUser(propertyId: string, userId: string): Promise<void> {
  await apiClient.post(`/properties/${propertyId}/users/${userId}/suspend`);
}

/** POST /api/v1/properties/:propertyId/users/:userId/reinstate */
export async function reinstateUser(propertyId: string, userId: string): Promise<void> {
  await apiClient.post(`/properties/${propertyId}/users/${userId}/reinstate`);
}

/** GET /api/v1/properties/:propertyId/users/pending-registrations */
export async function getPendingRegistrations(propertyId: string): Promise<PendingRegistration[]> {
  const r = await apiClient.get<PendingRegistration[]>(
    `/properties/${propertyId}/users/pending-registrations`,
  );
  return r.data;
}

/** POST /api/v1/properties/:propertyId/users/:userId/approve-registration */
export async function approveRegistration(propertyId: string, userId: string): Promise<void> {
  await apiClient.post(`/properties/${propertyId}/users/${userId}/approve-registration`);
}

/** POST /api/v1/properties/:propertyId/users/:userId/reject-registration */
export async function rejectRegistration(
  propertyId: string,
  userId: string,
  reason: string,
): Promise<void> {
  await apiClient.post(`/properties/${propertyId}/users/${userId}/reject-registration`, { reason });
}

/** PATCH /api/v1/properties/:propertyId/users/:userId/unit */
export async function assignUserUnit(
  propertyId: string,
  userId: string,
  unitId: string,
): Promise<void> {
  await apiClient.patch(`/properties/${propertyId}/users/${userId}/unit`, { unitId });
}

// ─── Property Manager — Modules ───────────────────────────────────────────────

// The server returns { id, moduleKey, isActive, toggledAt } per module — no
// human-friendly name/description/dependency info. We layer that on client-side
// from this lookup so the management UI has something to render.
const MODULE_META: Record<string, { name: string; description: string; alwaysOn?: boolean }> = {
  CORE: {
    name: 'Core access control',
    description: 'Visitor invitations, gate scanning, audit log.',
    alwaysOn: true,
  },
  COMPLIANCE_DPA: {
    name: 'Compliance & data protection',
    description: 'DSAR queue, retention sweeps, KDPA reporting.',
    alwaysOn: true,
  },
  ANALYTICS: { name: 'Analytics', description: 'Visit trends, peak hours, gate traffic.' },
  ANNOUNCEMENTS: {
    name: 'Announcements',
    description: 'Property-wide messages from management.',
  },
  BULK_EVENTS: {
    name: 'Get-togethers',
    description: 'Bulk invitations and signup links for events.',
  },
  DELIVERY: { name: 'Deliveries', description: 'Courier passes residents share via WhatsApp.' },
  DOMESTIC_WORKER: {
    name: 'Domestic help',
    description: 'Recurring passes for cleaners, gardeners, nannies.',
  },
  EMERGENCY: { name: 'Emergency lockdown', description: 'Manager can pause all access.' },
  GUARD_SCHEDULING: {
    name: 'Guard scheduling',
    description: 'Shift templates and weekly roster for security staff.',
  },
  INCIDENT_REPORTING: {
    name: 'Incident reporting',
    description: 'Guards log incidents from the gate; managers triage them.',
  },
  RECURRING_PASSES: {
    name: 'Recurring passes',
    description: 'Long-running access for regular visitors.',
  },
  TAXI: { name: 'Taxi & ride-share', description: 'Codes for Uber, Bolt, and pickups.' },
  WALK_IN: {
    name: 'Walk-in approvals',
    description: 'Visitors without a code can request entry from residents.',
  },
};

function humanizeModuleKey(key: string): string {
  return key
    .toLowerCase()
    .split('_')
    .map((s) => (s.length > 0 ? (s[0]?.toUpperCase() ?? '') + s.slice(1) : s))
    .join(' ');
}

/** GET /api/v1/properties/:propertyId/modules */
export async function getPropertyModules(propertyId: string): Promise<PropertyModule[]> {
  type ServerModule = {
    id: string;
    moduleKey: string;
    isActive: boolean;
    toggledAt: string | null;
  };
  const r = await apiClient.get<ServerModule[]>(`/properties/${propertyId}/modules`);
  return r.data.map((row) => {
    const meta = MODULE_META[row.moduleKey];
    return {
      id: row.id,
      moduleKey: row.moduleKey,
      name: meta?.name ?? humanizeModuleKey(row.moduleKey),
      description: meta?.description ?? '',
      active: row.isActive,
      alwaysOn: meta?.alwaysOn ?? false,
      dependsOn: [],
    };
  });
}

/** PATCH /api/v1/properties/:propertyId/modules/:moduleId */
export async function setModuleActive(
  propertyId: string,
  moduleId: string,
  active: boolean,
  confirm = false,
): Promise<void> {
  await apiClient.patch(`/properties/${propertyId}/modules/${moduleId}`, {
    active,
    ...(confirm ? { confirm: true } : {}),
  });
}

// ─── Property Manager — Property Config ──────────────────────────────────────

/** GET /api/v1/properties/:propertyId */
export async function getPropertyConfig(propertyId: string): Promise<PropertyConfig> {
  const r = await apiClient.get<PropertyConfig>(`/properties/${propertyId}`);
  return r.data;
}

/** PATCH /api/v1/properties/:propertyId */
export async function updatePropertyConfig(
  propertyId: string,
  body: Partial<Pick<PropertyConfig, 'name' | 'address' | 'propertyType' | 'registrationMode'>>,
): Promise<PropertyConfig> {
  const r = await apiClient.patch<PropertyConfig>(`/properties/${propertyId}`, body);
  return r.data;
}

/** GET /api/v1/properties/:propertyId/gates */
export async function getPmGates(propertyId: string): Promise<PropertyGate[]> {
  const r = await apiClient.get<PropertyGate[]>(`/properties/${propertyId}/gates`);
  return r.data;
}

/** POST /api/v1/properties/:propertyId/gates */
export async function createGate(
  propertyId: string,
  body: Omit<PropertyGate, 'id'>,
): Promise<PropertyGate> {
  const r = await apiClient.post<PropertyGate>(`/properties/${propertyId}/gates`, body);
  return r.data;
}

/** PATCH /api/v1/properties/:propertyId/gates/:gateId */
export async function updateGate(
  propertyId: string,
  gateId: string,
  body: Partial<Omit<PropertyGate, 'id'>>,
): Promise<PropertyGate> {
  const r = await apiClient.patch<PropertyGate>(`/properties/${propertyId}/gates/${gateId}`, body);
  return r.data;
}

/** DELETE /api/v1/properties/:propertyId/gates/:gateId */
export async function deleteGate(propertyId: string, gateId: string): Promise<void> {
  await apiClient.delete(`/properties/${propertyId}/gates/${gateId}`);
}

/** GET /api/v1/properties/:propertyId/units */
export async function getUnits(
  propertyId: string,
  params?: { page?: number; limit?: number },
): Promise<PaginatedResponse<Unit>> {
  const r = await apiClient.get<PaginatedResponse<Unit>>(`/properties/${propertyId}/units`, {
    params,
  });
  return r.data;
}

/** GET /api/v1/properties/:propertyId/units/zones */
export async function getZonesForUnits(
  propertyId: string,
): Promise<Array<{ id: string; name: string }>> {
  const r = await apiClient.get<Array<{ id: string; name: string }>>(
    `/properties/${propertyId}/units/zones`,
  );
  return r.data;
}

/** POST /api/v1/properties/:propertyId/units */
export async function createUnit(
  propertyId: string,
  body: {
    unitReference: string;
    zoneId: string;
    unitType: string;
    floor?: string | undefined;
    destinationLatitude?: number | null;
    destinationLongitude?: number | null;
  },
): Promise<Unit> {
  const r = await apiClient.post<Unit>(`/properties/${propertyId}/units`, body);
  return r.data;
}

/** PATCH /api/v1/properties/:propertyId/units/:unitId */
export async function updateUnit(
  propertyId: string,
  unitId: string,
  body: {
    unitReference?: string;
    zoneId?: string;
    unitType?: string;
    floor?: string | undefined;
    isActive?: boolean | undefined;
    destinationLatitude?: number | null;
    destinationLongitude?: number | null;
  },
): Promise<Unit> {
  const r = await apiClient.patch<Unit>(`/properties/${propertyId}/units/${unitId}`, body);
  return r.data;
}

/** DELETE /api/v1/properties/:propertyId/units/:unitId */
export async function deleteUnit(propertyId: string, unitId: string): Promise<void> {
  await apiClient.delete(`/properties/${propertyId}/units/${unitId}`);
}

/** GET /api/v1/properties/:propertyId/branding */
export async function getBranding(propertyId: string): Promise<BrandingConfig> {
  const r = await apiClient.get<BrandingConfig>(`/properties/${propertyId}/branding`);
  return r.data;
}

/** PATCH /api/v1/properties/:propertyId/branding */
export async function updateBranding(
  propertyId: string,
  body: Partial<
    Pick<BrandingConfig, 'primaryColour' | 'secondaryColour' | 'emailSenderName' | 'smsSenderId'>
  >,
): Promise<BrandingConfig> {
  const r = await apiClient.patch<BrandingConfig>(`/properties/${propertyId}/branding`, body);
  return r.data;
}

/** POST /api/v1/properties/:propertyId/branding/logo */
export async function uploadBrandingLogo(
  propertyId: string,
  file: File,
): Promise<{ uploaded: boolean; logoUrl: string }> {
  const formData = new FormData();
  formData.append('logo', file);
  const r = await apiClient.post<{ uploaded: boolean; logoUrl: string }>(
    `/properties/${propertyId}/branding/logo`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return r.data;
}

// ─── Property Manager — Announcements ────────────────────────────────────────

/** GET /api/v1/properties/:propertyId/announcements */
export async function getAnnouncements(propertyId: string): Promise<Announcement[]> {
  const r = await apiClient.get<Announcement[]>(`/properties/${propertyId}/announcements`);
  return r.data;
}

/** POST /api/v1/properties/:propertyId/announcements */
export async function createAnnouncement(
  propertyId: string,
  body: CreateAnnouncementBody,
): Promise<Announcement> {
  const r = await apiClient.post<Announcement>(`/properties/${propertyId}/announcements`, body);
  return r.data;
}

/** DELETE /api/v1/properties/:propertyId/announcements/:id */
export async function deleteAnnouncement(
  propertyId: string,
  announcementId: string,
): Promise<void> {
  await apiClient.delete(`/properties/${propertyId}/announcements/${announcementId}`);
}

// ─── Property Manager — Compliance / DPA ─────────────────────────────────────

/** GET /api/v1/properties/:propertyId/dsar */
export async function getDsarQueue(propertyId: string): Promise<DsarQueueItem[]> {
  const r = await apiClient.get<DsarQueueItem[]>(`/properties/${propertyId}/dsar`);
  return r.data;
}

/** PATCH /api/v1/properties/:propertyId/dsar/:dsarId — PM updates DSAR status */
export async function updateDsarStatus(
  propertyId: string,
  dsarId: string,
  body: { status: PmDsarTransition; reason?: string },
): Promise<{ id: string; status: PmDsarTransition }> {
  const r = await apiClient.patch<{ id: string; status: PmDsarTransition }>(
    `/properties/${propertyId}/dsar/${dsarId}`,
    body,
  );
  return r.data;
}

/** POST /api/v1/properties/:propertyId/retention/trigger — manually trigger data retention sweep */
export async function triggerRetentionSweep(propertyId: string): Promise<void> {
  await apiClient.post(`/properties/${propertyId}/retention/trigger`);
}

/** GET /api/v1/properties/:propertyId/retention/status */
export interface RetentionSweepEntry {
  id: string;
  runAt: string;
  recordsAnonymised: number;
  recordsProcessed: number;
  status: string;
}

export interface RetentionStatus {
  retentionDays: number;
  lastSweepAt: string | null;
  lastSweepStatus: string | null;
  lastSweepError: string | null;
  recordsAnonymised: number;
  recordsProcessed: number;
  nextRunAt: string | null;
  recentSweeps: RetentionSweepEntry[];
}

export async function getRetentionStatus(propertyId: string): Promise<RetentionStatus> {
  const r = await apiClient.get<RetentionStatus>(`/properties/${propertyId}/retention/status`);
  return r.data;
}

// ─── Property Manager — Analytics ────────────────────────────────────────────

/** GET /api/v1/properties/:propertyId/analytics/summary */
export async function getAnalyticsSummary(
  propertyId: string,
  params: { dateFrom: string; dateTo: string },
): Promise<AnalyticsSummary> {
  const r = await apiClient.get<AnalyticsSummary>(`/properties/${propertyId}/analytics/summary`, {
    params,
  });
  return r.data;
}

/** GET /api/v1/properties/:propertyId/analytics/gate-traffic */
export async function getGateTraffic(
  propertyId: string,
  params: { dateFrom: string; dateTo: string },
): Promise<GateTrafficPoint[]> {
  const r = await apiClient.get<GateTrafficPoint[]>(
    `/properties/${propertyId}/analytics/gate-traffic`,
    { params },
  );
  return r.data;
}

/** GET /api/v1/properties/:propertyId/analytics/visitor-types */
export async function getVisitorTypeBreakdown(
  propertyId: string,
  params: { dateFrom: string; dateTo: string },
): Promise<VisitorTypeBreakdown[]> {
  const r = await apiClient.get<VisitorTypeBreakdown[]>(
    `/properties/${propertyId}/analytics/visitor-types`,
    { params },
  );
  return r.data;
}

/** GET /api/v1/properties/:propertyId/analytics/heatmap */
export async function getHeatmap(
  propertyId: string,
  params: { dateFrom: string; dateTo: string },
): Promise<HeatmapCell[]> {
  const r = await apiClient.get<HeatmapCell[]>(`/properties/${propertyId}/analytics/heatmap`, {
    params,
  });
  return r.data;
}

// ─── Property Manager — Emergency Lockdown ───────────────────────────────────

/** GET /api/v1/properties/:propertyId/emergency/status */
export async function getLockdownStatus(propertyId: string): Promise<LockdownStatus> {
  const r = await apiClient.get<LockdownStatus>(`/properties/${propertyId}/emergency/status`);
  return r.data;
}

/** POST /api/v1/properties/:propertyId/emergency/lockdown */
export async function triggerLockdown(
  propertyId: string,
  body: { lockdownType: EmergencyType; reason?: string; confirm: true },
): Promise<void> {
  await apiClient.post(`/properties/${propertyId}/emergency/lockdown`, body);
}

/** POST /api/v1/properties/:propertyId/emergency/lift */
export async function liftLockdown(
  propertyId: string,
  body: { reason?: string; confirm: true },
): Promise<void> {
  await apiClient.post(`/properties/${propertyId}/emergency/lift`, body);
}

/** GET /api/v1/properties/:propertyId/emergency/lockdown */
export async function listLockdowns(propertyId: string): Promise<LockdownRecord[]> {
  const r = await apiClient.get<LockdownRecord[]>(`/properties/${propertyId}/emergency/lockdown`);
  return r.data;
}

// ─── Property Manager — Guard Detail ─────────────────────────────────────────

/** GET /api/v1/properties/:propertyId/users/:userId */
export async function getPmUser(propertyId: string, userId: string): Promise<PmUserDetail> {
  const r = await apiClient.get<PmUserDetail>(`/properties/${propertyId}/users/${userId}`);
  return r.data;
}

/** GET /api/v1/properties/:propertyId/guards/activity */
export async function getGuardActivity(
  propertyId: string,
  params?: { guardUserId?: string; dateFrom?: string; dateTo?: string },
): Promise<GuardActivityEvent[]> {
  const r = await apiClient.get<GuardActivityEvent[]>(`/properties/${propertyId}/guards/activity`, {
    params,
  });
  return r.data;
}

/** GET /api/v1/properties/:propertyId/devices — filtered client-side by guardUserId */
export async function getGuardDevices(
  propertyId: string,
  guardUserId: string,
): Promise<GuardDevice[]> {
  const r = await apiClient.get<GuardDevice[]>(`/properties/${propertyId}/devices`);
  return r.data.filter((d) => d.user.id === guardUserId);
}

/** GET /api/v1/properties/:propertyId/devices — returns ALL devices, not filtered by a guard. */
export async function getAllDevices(propertyId: string): Promise<GuardDevice[]> {
  const r = await apiClient.get<GuardDevice[]>(`/properties/${propertyId}/devices`);
  return r.data;
}

/** POST /api/v1/properties/:propertyId/devices/:deviceId/invalidate
 *  Pass GuardDevice.id (session UUID) — not GuardDevice.deviceId (hardware token). */
export async function invalidateGuardDevice(propertyId: string, deviceId: string): Promise<void> {
  await apiClient.post(`/properties/${propertyId}/devices/${deviceId}/invalidate`);
}

// ─── Property Manager — Shift Templates ──────────────────────────────────────

/** GET /api/v1/properties/:propertyId/shift-templates */
export async function listShiftTemplates(propertyId: string): Promise<ShiftTemplate[]> {
  const r = await apiClient.get<ShiftTemplate[]>(`/properties/${propertyId}/shift-templates`);
  return r.data;
}

/** POST /api/v1/properties/:propertyId/shift-templates */
export async function createShiftTemplate(
  propertyId: string,
  body: CreateShiftTemplateBody,
): Promise<ShiftTemplate> {
  const r = await apiClient.post<ShiftTemplate>(`/properties/${propertyId}/shift-templates`, body);
  return r.data;
}

/** PATCH /api/v1/properties/:propertyId/shift-templates/:id */
export async function updateShiftTemplate(
  propertyId: string,
  id: string,
  body: UpdateShiftTemplateBody,
): Promise<ShiftTemplate> {
  const r = await apiClient.patch<ShiftTemplate>(
    `/properties/${propertyId}/shift-templates/${id}`,
    body,
  );
  return r.data;
}

/** DELETE /api/v1/properties/:propertyId/shift-templates/:id — 204 */
export async function deleteShiftTemplate(propertyId: string, id: string): Promise<void> {
  await apiClient.delete(`/properties/${propertyId}/shift-templates/${id}`);
}

// ─── Property Manager — Roster ───────────────────────────────────────────────

/** GET /api/v1/properties/:propertyId/rosters */
export async function listRosters(
  propertyId: string,
  params?: { dateFrom?: string; dateTo?: string },
): Promise<RosterEntry[]> {
  const r = await apiClient.get<RosterEntry[]>(`/properties/${propertyId}/rosters`, { params });
  return r.data;
}

/** POST /api/v1/properties/:propertyId/rosters */
export async function createRoster(
  propertyId: string,
  body: CreateRosterBody,
): Promise<RosterEntry> {
  const r = await apiClient.post<RosterEntry>(`/properties/${propertyId}/rosters`, body);
  return r.data;
}

/** DELETE /api/v1/properties/:propertyId/rosters/:id — 204 */
export async function deleteRoster(propertyId: string, id: string): Promise<void> {
  await apiClient.delete(`/properties/${propertyId}/rosters/${id}`);
}

// ─── Organisation Admin ───────────────────────────────────────────────────────

/** GET /api/v1/organisations/:orgId/dashboard */
export async function getOrgDashboard(orgId: string): Promise<OrgDashboard> {
  const r = await apiClient.get<OrgDashboard>(`/organisations/${orgId}/dashboard`);
  return r.data;
}

/** GET /api/v1/organisations/:orgId/properties */
export async function listOrgProperties(orgId: string): Promise<OrgProperty[]> {
  const r = await apiClient.get<OrgProperty[]>(`/organisations/${orgId}/properties`);
  return r.data;
}

/** POST /api/v1/organisations/:orgId/properties */
export async function createOrgProperty(
  orgId: string,
  body: CreateOrgPropertyBody,
): Promise<OrgProperty> {
  const r = await apiClient.post<OrgProperty>(`/organisations/${orgId}/properties`, body);
  return r.data;
}

/** GET /api/v1/organisations/:orgId/users — returns org_admin users only */
export async function listOrgAdmins(orgId: string): Promise<OrgAdmin[]> {
  const r = await apiClient.get<OrgAdmin[]>(`/organisations/${orgId}/users`);
  return r.data;
}

/** POST /api/v1/organisations/:orgId/users */
export async function createOrgAdmin(
  orgId: string,
  body: CreateOrgAdminBody,
): Promise<CreateOrgAdminResult> {
  const r = await apiClient.post<CreateOrgAdminResult>(`/organisations/${orgId}/users`, body);
  return r.data;
}

// ─── Super Admin — Health ──────────────────────────────────────────────────

export const getAdminHealth = (): Promise<AdminHealth> =>
  apiClient.get('/admin/health').then((r) => r.data as AdminHealth);

export const getAdminJobs = (): Promise<BullMQQueue[]> =>
  apiClient
    .get<{ queues: BullMQQueue[]; timestamp: string }>('/admin/jobs')
    .then((r) => r.data.queues);

export const getAdminErrors = (): Promise<{ errors: AdminError[]; message: string }> =>
  apiClient.get('/admin/errors').then((r) => r.data as { errors: AdminError[]; message: string });

export const getAdminIncidents = (): Promise<AdminIncidentSummary> =>
  apiClient.get('/admin/incidents').then((r) => r.data as AdminIncidentSummary);

// ─── Super Admin — Audit Log ──────────────────────────────────────────────

export const getAuditLog = (
  filters: AuditLogFilters,
): Promise<CursorPaginatedResponse<AuditLogEntry>> =>
  apiClient
    .get('/admin/audit-log', { params: filters })
    .then((r) => r.data as CursorPaginatedResponse<AuditLogEntry>);

// ─── Super Admin — Impersonation ──────────────────────────────────────────

export const startImpersonation = (body: StartImpersonationBody): Promise<ImpersonationSession> =>
  apiClient.post('/admin/impersonate', body).then((r) => r.data as ImpersonationSession);

export const endImpersonation = (): Promise<void> =>
  apiClient.delete('/admin/impersonate').then(() => undefined);

// ─── Super Admin — Feature Flags ──────────────────────────────────────────

export const getFeatureFlags = (): Promise<FeatureFlag[]> =>
  apiClient.get('/admin/feature-flags').then((r) => r.data as FeatureFlag[]);

export const updateFeatureFlags = (body: UpdateFeatureFlagsBody): Promise<FeatureFlag[]> =>
  apiClient.patch('/admin/feature-flags', body).then((r) => r.data as FeatureFlag[]);

export const updatePropertyFeatureFlags = (
  propertyId: string,
  body: UpdateFeatureFlagsBody,
): Promise<FeatureFlag[]> =>
  apiClient
    .patch(`/admin/properties/${propertyId}/feature-flags`, body)
    .then((r) => r.data as FeatureFlag[]);

// ─── Super Admin — Platform Config ────────────────────────────────────────

export const getPlatformConfig = (): Promise<PlatformConfig> =>
  apiClient.get('/admin/config').then((r) => r.data as PlatformConfig);

export const updatePlatformConfig = (body: UpdatePlatformConfigBody): Promise<PlatformConfig> =>
  apiClient.patch('/admin/config', body).then((r) => r.data as PlatformConfig);

// ─── Super Admin — Organisations ──────────────────────────────────────────

export const getOrganisations = (): Promise<Organisation[]> =>
  apiClient.get('/organisations').then((r) => r.data as Organisation[]);

export const createOrganisation = (body: CreateOrganisationBody): Promise<Organisation> =>
  apiClient.post('/organisations', body).then((r) => r.data as Organisation);

export const getOrganisationUsers = (orgId: string): Promise<OrgUser[]> =>
  apiClient.get(`/organisations/${orgId}/users`).then((r) => r.data as OrgUser[]);

export const suspendOrganisation = (orgId: string, confirmName: string): Promise<Organisation> =>
  apiClient
    .post(`/organisations/${orgId}/suspend`, { name: confirmName })
    .then((r) => r.data as Organisation);

export const reinstateOrganisation = (orgId: string): Promise<Organisation> =>
  apiClient.post(`/organisations/${orgId}/reinstate`).then((r) => r.data as Organisation);

export const updateOrganisation = (
  orgId: string,
  body: { name: string; contactName: string; contactEmail: string; type: string },
): Promise<Organisation> =>
  apiClient.patch(`/organisations/${orgId}`, body).then((r) => r.data as Organisation);

export const resendActivation = (
  orgId: string,
): Promise<{ status: string; email: string; expiresAt: string }> =>
  apiClient
    .post(`/organisations/${orgId}/resend-activation`)
    .then((r) => r.data as { status: string; email: string; expiresAt: string });

// ─── Super Admin — Property Decommission / Reactivate ─────────────────────

export interface DecommissionPropertyResult {
  status: string;
  propertyId: string;
  propertyName: string;
  orgAlsoDecommissioned: boolean;
  organisationId: string;
}

export interface ReactivatePropertyResult {
  status: string;
  propertyId: string;
  propertyName: string;
  orgAlsoReactivated: boolean;
  organisationId: string;
}

export const decommissionProperty = (
  propertyId: string,
  confirmName: string,
): Promise<DecommissionPropertyResult> =>
  apiClient
    .post(`/admin/properties/${propertyId}/decommission`, { confirmName })
    .then((r) => r.data as DecommissionPropertyResult);

export const reactivateProperty = (propertyId: string): Promise<ReactivatePropertyResult> =>
  apiClient
    .post(`/admin/properties/${propertyId}/reactivate`)
    .then((r) => r.data as ReactivatePropertyResult);

/** POST /api/v1/admin/properties/:propertyId/invite-manager — super_admin bypass */
export const adminInvitePropertyManager = (
  propertyId: string,
  body: { fullName: string; email: string; phone?: string },
): Promise<void> =>
  apiClient.post(`/admin/properties/${propertyId}/invite-manager`, body).then(() => undefined);

// ─── Super Admin — Enterprise customer provisioning (ADR 010) ────────────

export type ProvisionEnterpriseCustomerBody = {
  fullName: string;
  email: string;
  companyName: string;
  buildingAddress: string;
  subdomain?: string;
  phone?: string;
};

export type ProvisionEnterpriseCustomerResult = {
  status: 'pending_activation';
  email: string;
  subdomain: string;
  organisationId: string;
  propertyId: string;
};

export const provisionEnterpriseCustomer = (
  body: ProvisionEnterpriseCustomerBody,
): Promise<ProvisionEnterpriseCustomerResult> =>
  apiClient
    .post('/organisations/enterprise-customers', body)
    .then((r) => r.data as ProvisionEnterpriseCustomerResult);

// ─── Super Admin — Estate customer provisioning ───────────────────────────

export type ProvisionEstateCustomerBody = {
  fullName: string;
  email: string;
  estateName: string;
  estateAddress: string;
  propertyType:
    | 'gated_community'
    | 'residential_apartment'
    | 'master_planned_city'
    | 'mixed_use'
    | 'other';
  subdomain?: string;
  phone?: string;
};

export type ProvisionEstateCustomerResult = {
  status: 'pending_activation';
  email: string;
  subdomain: string;
  organisationId: string;
  propertyId: string;
};

export const provisionEstateCustomer = (
  body: ProvisionEstateCustomerBody,
): Promise<ProvisionEstateCustomerResult> =>
  apiClient
    .post('/organisations/estate-customers', body)
    .then((r) => r.data as ProvisionEstateCustomerResult);

// ─── Guard Web App ────────────────────────────────────────────────────────────

/** GET /api/v1/guard/active-visits — currently checked-in visitors at the guard's gate */
export async function getGuardActiveVisits(gateId?: string): Promise<CheckedInVisitor[]> {
  const r = await apiClient.get<CheckedInVisitor[]>('/guard/active-visits', {
    params: gateId ? { gateId } : undefined,
  });
  return r.data;
}

/** GET /api/v1/guard/upcoming-visitors — visitors expected in the next 2 hours */
export async function getGuardUpcomingVisitors(gateId?: string): Promise<UpcomingVisitor[]> {
  const r = await apiClient.get<UpcomingVisitor[]>('/guard/upcoming-visitors', {
    params: gateId ? { gateId } : undefined,
  });
  return r.data;
}

/** POST /api/v1/guard/checkout/:visitId — check out a visitor */
export async function checkoutVisitor(visitId: string): Promise<void> {
  await apiClient.post(`/guard/checkout/${visitId}`);
}

/** POST /api/v1/guard/visits/:visitId/acknowledge-flag — guard "set aside" of a flagged visit */
export async function acknowledgeVisitFlag(
  visitId: string,
): Promise<{ visitId: string; isFlagged: boolean; acknowledgedAt: string | null }> {
  const r = await apiClient.post(`/guard/visits/${visitId}/acknowledge-flag`);
  return r.data as { visitId: string; isFlagged: boolean; acknowledgedAt: string | null };
}

/** POST /api/v1/properties/:propertyId/access-events/:visitId/flag — flag a visit (detain on exit) */
export async function flagVisit(
  propertyId: string,
  visitId: string,
  reason: string,
): Promise<VisitFlag> {
  const r = await apiClient.post(`/properties/${propertyId}/access-events/${visitId}/flag`, {
    reason,
  });
  return r.data as VisitFlag;
}

/** POST /api/v1/properties/:propertyId/access-events/:visitId/flag/clear — clear a visit flag */
export async function clearVisitFlag(propertyId: string, visitId: string): Promise<VisitFlag> {
  const r = await apiClient.post(`/properties/${propertyId}/access-events/${visitId}/flag/clear`);
  return r.data as VisitFlag;
}

/** POST /api/v1/guard/walk-in — create a walk-in request for an unannounced visitor */
export async function createWalkIn(body: CreateWalkInBody): Promise<WalkInRequest> {
  const r = await apiClient.post<WalkInRequest>('/guard/walk-in', body);
  return r.data;
}

/** GET /api/v1/guard/walk-in/units/:unitId/employees — list active employees for an enterprise unit */
export async function getGuardUnitEmployees(
  unitId: string,
): Promise<Array<{ id: string; fullName: string }>> {
  const r = await apiClient.get<Array<{ id: string; fullName: string }>>(
    `/guard/walk-in/units/${unitId}/employees`,
  );
  return r.data;
}

/** GET /api/v1/guard/units/search?q= — list units accessible to the guard role for walk-in registration */
export async function getGuardUnits(
  q = '',
): Promise<Array<{ id: string; unitReference: string; zoneName: string | null }>> {
  const r = await apiClient.get<
    Array<{ id: string; unitReference: string; zoneName: string | null }>
  >('/guard/units/search', { params: { q } });
  return r.data;
}

/** GET /api/v1/guard/walk-in/:requestId/status — poll walk-in approval status from the guard's gate */
export async function getWalkInStatus(requestId: string): Promise<WalkInRequest> {
  const r = await apiClient.get<{ status: string; autoTimeoutAt?: string }>(
    `/guard/walk-in/${requestId}/status`,
  );
  const { status, autoTimeoutAt } = r.data;
  // Server uses 'timed_out' (DB enum value); client type uses 'expired'.
  const mappedStatus = status === 'timed_out' ? 'expired' : (status as WalkInRequest['status']);
  return {
    ...r.data,
    status: mappedStatus,
    expiresAt: autoTimeoutAt ?? '',
  } as unknown as WalkInRequest;
}

/** POST /api/v1/guard/walk-in/:requestId/cancel — guard cancels a pending walk-in they created */
export async function cancelGuardWalkIn(requestId: string): Promise<void> {
  await apiClient.post(`/guard/walk-in/${requestId}/cancel`);
}

/** GET /api/v1/guard/daily-log — cursor-paginated access log for today's shift */
export async function getGuardDailyLog(
  params: GuardDailyLogParams,
): Promise<CursorPaginatedResponse<AccessEvent>> {
  const r = await apiClient.get<CursorPaginatedResponse<AccessEvent>>('/guard/daily-log', {
    params,
  });
  return r.data;
}
/** POST /api/v1/guard/incidents — log an incident at the gate */
export async function createGuardIncident(body: {
  incidentType: string;
  description: string;
  title?: string;
  photoReference?: string;
}): Promise<IncidentResult> {
  const r = await apiClient.post<IncidentResult>('/guard/incidents', body);
  return r.data;
}
/** POST /api/v1/guard/incidents/photo — upload an incident photo */
export async function uploadIncidentPhoto(file: File): Promise<{ objectKey: string }> {
  const form = new FormData();
  form.append('photo', file);
  const r = await apiClient.post<{ objectKey: string }>('/guard/incidents/photo', form);
  return r.data;
}

/** POST /api/v1/guard/shifts/start — start a guard shift at a gate */
export async function startGuardShift(body: StartShiftBody): Promise<ShiftInfo> {
  const r = await apiClient.post<ShiftInfo>('/guard/shifts/start', body);
  return r.data;
}

/** POST /api/v1/guard/shifts/change — handover to incoming guard (Mode B shared device) */
export async function changeGuardShift(body: ChangeShiftBody): Promise<ShiftInfo> {
  const r = await apiClient.post<ShiftInfo>('/guard/shifts/change', body);
  return r.data;
}

/** POST /api/v1/guard/pin/validate — validate a 6-digit PIN.
 * NOTE: PIN validation is a single-step operation on the server — it both
 * validates the PIN and creates the access event in one call (unlike QR which
 * is validate → confirm). The response includes accessEventId directly.
 */
export async function validatePin(pin: string, gateId?: string): Promise<PinValidationResult> {
  const r = await apiClient.post<{
    valid: boolean;
    accessEventId?: string;
    checkedInAt?: string;
    reason?: string;
  }>('/guard/pin/validate', {
    pin,
    deviceId: 'web',
    ...(gateId ? { gateId } : {}),
  });
  const data = r.data;
  // Map server shape → PinValidationResult consumed by PinValidationSection
  if (data.valid && data.accessEventId) {
    return {
      result: 'allow',
      visitId: data.accessEventId,
    };
  }
  return {
    result: 'deny',
    reason: data.reason ?? 'PIN_NOT_FOUND_OR_LOCKED',
  };
}

/** POST /api/v1/guard/qr/validate — validate a QR token scanned from a visitor's invitation.
 * Read-only — no access event created. Call confirmEntry() to complete the check-in.
 */
export async function validateQrToken(
  token: string,
  gateId?: string,
): Promise<PinValidationResult> {
  const r = await apiClient.post<{
    valid: boolean;
    passType?: string;
    reason?: string;
    invitation?: {
      id: string;
      visitorName: string;
      invitationType: string;
      validFrom: string | null;
      validUntil: string;
      unit?: string;
      partySize: number;
      vehiclePlate?: string | null;
      vehicleMake?: string | null;
      vehicleColour?: string | null;
    };
    pass?: {
      id: string;
      visitorName?: string;
      workerName?: string;
      passSubType?: string;
      windowStart?: string;
      windowEnd?: string;
      unit?: string;
      crewSize?: number;
    };
    isContractor?: boolean;
    crewSize?: number;
    crewInsideNow?: number;
    crewPhotoNeededThisWeek?: boolean;
  }>('/guard/qr/validate', {
    token,
    deviceId: 'web',
    ...(gateId ? { gateId } : {}),
  });
  const data = r.data;
  // Map server shape → PinValidationResult consumed by QrScannerSection
  if (!data.valid) {
    return { result: 'deny', reason: data.reason ?? 'INVALID_QR' };
  }
  // Use the QR token as visitId — passed back to confirmEntry to complete the check-in
  const inv = data.invitation;
  const pass = data.pass;
  const visitorName = inv?.visitorName ?? pass?.visitorName ?? pass?.workerName ?? '';
  const unit = inv?.unit ?? pass?.unit ?? '';
  // Contractor crew: surface crewSize as partySize so the guard's existing
  // headcount stepper drives actualPartySize (workers arriving in this batch).
  const crewSize = data.isContractor ? (data.crewSize ?? pass?.crewSize) : undefined;
  return {
    result: 'allow',
    visitId: token,
    ...(visitorName ? { visitor: { name: visitorName, photoUrl: null as string | null } } : {}),
    ...(unit ? { host: { name: '', unit } } : {}),
    invitationType: (inv?.invitationType ?? 'single_visit') as InvitationType,
    ...(inv?.partySize !== undefined ? { partySize: inv.partySize } : {}),
    ...(crewSize !== undefined ? { partySize: crewSize } : {}),
    ...(data.isContractor ? { isContractor: true } : {}),
    ...(data.crewInsideNow !== undefined ? { crewInsideNow: data.crewInsideNow } : {}),
    ...(inv?.validFrom !== undefined && inv.validFrom !== null ? { validFrom: inv.validFrom } : {}),
    ...(inv?.validUntil !== undefined ? { validUntil: inv.validUntil } : {}),
    ...(inv?.vehiclePlate
      ? {
          vehicleDetails: [inv.vehicleMake, inv.vehiclePlate, inv.vehicleColour]
            .filter(Boolean)
            .join(' '),
        }
      : {}),
  };
}

/** POST /api/v1/guard/qr/confirm — confirm QR entry — creates access event.
 * Called after validateQrToken returns result='allow'.
 * The token parameter should be the QR token from the visitor's code.
 */
export async function confirmEntry(
  token: string,
  actualPartySize?: number,
): Promise<ConfirmEntryResult> {
  const r = await apiClient.post<{ accessEventId: string; checkedInAt: string }>(
    '/guard/qr/confirm',
    {
      token,
      entryMethod: 'qr_scan',
      deviceId: 'web',
      ...(actualPartySize !== undefined ? { actualPartySize } : {}),
    },
  );
  // Map accessEventId → visitId for ConfirmEntryResult
  return { visitId: r.data.accessEventId, checkedInAt: r.data.checkedInAt };
}

// ── Contractor crew sessions ────────────────────────────────────────────────

export interface CrewSession {
  id: string;
  sessionDate: string;
  contractorName: string;
  recurringPassId: string | null;
  unitReference: string;
  expectedCrew: number;
  totalIn: number;
  totalOut: number;
  insideCount: number;
  status: 'open' | 'reconciled' | 'unreconciled' | 'closed_manual';
  hasPhoto: boolean;
  firstEntryAt: string | null;
  lastExitAt: string | null;
  reconciledAt: string | null;
}

/** POST /api/v1/guard/crew/:crewSessionId/checkout — record a batch of crew leaving. */
export async function crewCheckout(
  crewSessionId: string,
  headcountOut: number,
): Promise<{ crewSessionId: string; insideCount: number; reconciled: boolean }> {
  const r = await apiClient.post<{
    crewSessionId: string;
    insideCount: number;
    reconciled: boolean;
  }>(`/guard/crew/${crewSessionId}/checkout`, { headcountOut });
  return r.data;
}

export interface ActiveCrew {
  id: string;
  contractorName: string;
  unitReference: string;
  expectedCrew: number;
  totalIn: number;
  insideCount: number;
}

/** GET /api/v1/guard/crew/active — contractor crews still on site today. */
export async function getActiveCrews(): Promise<ActiveCrew[]> {
  const r = await apiClient.get<ActiveCrew[]>('/guard/crew/active');
  return r.data;
}

/** GET /api/v1/properties/:propertyId/crew-sessions — PM reconciliation list. */
export async function listCrewSessions(
  propertyId: string,
  params?: { status?: string; date?: string },
): Promise<CrewSession[]> {
  const r = await apiClient.get<CrewSession[]>(`/properties/${propertyId}/crew-sessions`, {
    params,
  });
  return r.data;
}

/** POST /api/v1/properties/:propertyId/crew-sessions/:id/close — PM manual close. */
export async function closeCrewSession(
  propertyId: string,
  crewSessionId: string,
  reason: string,
): Promise<{ id: string; status: string; reconciledAt: string | null }> {
  const r = await apiClient.post<{ id: string; status: string; reconciledAt: string | null }>(
    `/properties/${propertyId}/crew-sessions/${crewSessionId}/close`,
    { reason },
  );
  return r.data;
}

/** GET /api/v1/properties/:propertyId/crew-sessions/:id/photo — signed photo URL. */
export async function getCrewSessionPhotoUrl(
  propertyId: string,
  crewSessionId: string,
): Promise<{ url: string }> {
  const r = await apiClient.get<{ url: string }>(
    `/properties/${propertyId}/crew-sessions/${crewSessionId}/photo`,
  );
  return r.data;
}

/** GET /api/v1/guard/me — get guard identity from device session + active shift (Mode B) */
export async function getGuardDeviceMe(): Promise<{
  guardId: string | null;
  guardName: string | null;
  propertyId: string;
  propertyName: string;
  gateId: string;
  gateName: string;
  shiftActive: boolean;
  shiftStartedAt: string | null;
  role: 'guard';
  authMode: 'device_session';
}> {
  const r = await apiClient.get('/guard/me');
  return r.data;
}
/** GET /api/v1/guard/module-state — check guard web app module activation state */
export async function getGuardModuleState(): Promise<{ invitesEnabled: boolean }> {
  const r = await apiClient.get<{ invitesEnabled: boolean }>('/guard/module-state');
  return r.data;
}

/** GET /api/v1/guard/emergency/status — check active lockdown status for the guard's property */
export async function getGuardEmergencyStatus(): Promise<{
  active: boolean;
  type: string | null;
  triggeredAt: string | null;
  triggeredBy: string | null;
}> {
  const r = await apiClient.get<{
    active: boolean;
    type: string | null;
    triggeredAt: string | null;
    triggeredBy: string | null;
  }>('/guard/emergency/status');
  return r.data;
}

// ─── Intercom ─────────────────────────────────────────────────────────────────

export interface IntercomIdentity {
  userJwt: string | null;
}

/** GET /api/v1/intercom/identity — returns a signed JWT for the Messenger. */
export async function getIntercomIdentity(): Promise<IntercomIdentity> {
  const r = await apiClient.get<IntercomIdentity>('/intercom/identity');
  return r.data;
}

// ─── Super Admin — Failed Notifications & Resend ─────────────────────────────

export interface FailedNotification {
  id: string;
  channel: string;
  status: string;
  subject: string | null;
  template_key: string | null;
  failure_reason: string | null;
  retry_count: number;
  created_at: string;
  failed_at: string | null;
  user: { id: string; email: string; full_name: string } | null;
  property: { id: string; name: string } | null;
}

export interface FailedNotificationsResponse {
  items: FailedNotification[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/** GET /api/v1/admin/notifications/failed — list permanently failed notifications */
export async function getFailedNotifications(
  page = 1,
  channel?: string,
): Promise<FailedNotificationsResponse> {
  const r = await apiClient.get<FailedNotificationsResponse>('/admin/notifications/failed', {
    params: { page, ...(channel ? { channel } : {}) },
  });
  return r.data;
}

/** POST /api/v1/admin/notifications/:id/resend — re-queue a failed notification */
export async function resendNotification(
  notificationId: string,
): Promise<{ status: string; notificationId: string; jobId: string }> {
  const r = await apiClient.post(`/admin/notifications/${notificationId}/resend`);
  return r.data as { status: string; notificationId: string; jobId: string };
}

export const deleteOrganisation = (
  orgId: string,
  confirmName: string,
): Promise<{ deleted: boolean }> =>
  apiClient
    .delete(`/organisations/${orgId}`, { data: { name: confirmName } })
    .then((r) => r.data as { deleted: boolean });

// ─── Mode B — Shared Device Authentication ───────────────────────────────────

/** POST /properties/:id/devices/pair — generate a pairing code for a gate device */
export async function generatePairingCode(
  propertyId: string,
  body: { gateId: string; deviceName?: string },
): Promise<{ code: string; expiresAt: string; gateId: string; gateName: string }> {
  const r = await apiClient.post(`/properties/${propertyId}/devices/pair`, body);
  return r.data as { code: string; expiresAt: string; gateId: string; gateName: string };
}

/** POST /auth/device/activate — activate a device with a pairing code */
export async function activateDevice(
  code: string,
): Promise<{ gateId: string; gateName: string; propertyId: string; propertyName: string }> {
  const r = await apiClient.post('/auth/device/activate', { code });
  const data = r.data as {
    gateId: string;
    gateName: string;
    propertyId: string;
    propertyName: string;
    sessionToken?: string;
  };
  // Store the device session token in localStorage for header-based auth.
  // Cross-origin cookies are unreliable on mobile browsers, so we send the
  // token as X-Device-Session header on subsequent requests instead.
  if (data.sessionToken) {
    localStorage.setItem('sg_device_session', data.sessionToken);
  }
  return data;
}

/** GET /guard/roster — list active roster guards for the device's gate */
export async function getGuardRoster(): Promise<{
  guards: Array<{ id: string; fullName: string; badgeNumber: string | null }>;
  gateName: string;
  propertyName: string;
}> {
  const r = await apiClient.get('/guard/roster');
  return r.data as {
    guards: Array<{ id: string; fullName: string; badgeNumber: string | null }>;
    gateName: string;
    propertyName: string;
  };
}

/** POST /guard/shifts/select — guard selects themselves to start shift (Mode B) */
export async function selectShift(guardId: string): Promise<{
  shiftId: string;
  guardId: string;
  guardName: string;
  gateId: string;
  gateName: string;
  startedAt: string;
}> {
  const r = await apiClient.post('/guard/shifts/select', { guardId });
  return r.data as {
    shiftId: string;
    guardId: string;
    guardName: string;
    gateId: string;
    gateName: string;
    startedAt: string;
  };
}

/** POST /guard/shifts/end — end current shift on shared device */
export async function endGuardShift(): Promise<{ status: string }> {
  const r = await apiClient.post('/guard/shifts/end');
  return r.data as { status: string };
}

/** POST /properties/:id/guards/roster — create a roster guard (name only) */
export async function createRosterGuard(
  propertyId: string,
  body: { fullName: string; badgeNumber?: string; gateId: string },
): Promise<{
  id: string;
  fullName: string;
  badgeNumber: string | null;
  gateId: string;
  gateName: string;
  authMode: string;
  status: string;
}> {
  const r = await apiClient.post(`/properties/${propertyId}/guards/roster`, body);
  return r.data as {
    id: string;
    fullName: string;
    badgeNumber: string | null;
    gateId: string;
    gateName: string;
    authMode: string;
    status: string;
  };
}

/** PATCH /properties/:id/guards/roster/:guardId — update a roster guard */
export async function updateRosterGuard(
  propertyId: string,
  guardId: string,
  body: Partial<{ fullName: string; badgeNumber: string; gateId: string }>,
): Promise<{ status: string }> {
  const r = await apiClient.patch(`/properties/${propertyId}/guards/roster/${guardId}`, body);
  return r.data as { status: string };
}

/** POST /properties/:id/guards/roster/:guardId/suspend — suspend a roster guard */
export async function suspendRosterGuard(
  propertyId: string,
  guardId: string,
): Promise<{ status: string }> {
  const r = await apiClient.post(`/properties/${propertyId}/guards/roster/${guardId}/suspend`);
  return r.data as { status: string };
}

/** DELETE /properties/:id/guards/roster/:guardId — remove a roster guard */
export async function removeRosterGuard(
  propertyId: string,
  guardId: string,
): Promise<{ status: string }> {
  const r = await apiClient.delete(`/properties/${propertyId}/guards/roster/${guardId}`);
  return r.data as { status: string };
}

/** DELETE /properties/:id/devices/:sessionId — deregister a gate device */
export async function deregisterDevice(
  propertyId: string,
  deviceSessionId: string,
): Promise<{ status: string }> {
  const r = await apiClient.delete(`/properties/${propertyId}/devices/${deviceSessionId}`);
  return r.data as { status: string };
}

/** PATCH /properties/:id/devices/:sessionId — rename a gate device */
export async function renameDevice(
  propertyId: string,
  deviceSessionId: string,
  deviceName: string,
): Promise<{ status: string }> {
  const r = await apiClient.patch(`/properties/${propertyId}/devices/${deviceSessionId}`, {
    deviceName,
  });
  return r.data as { status: string };
}

// ─── Trusted Operators ────────────────────────────────────────────────────────

export interface TrustedOperator {
  id: string;
  name: string;
  operator_type: 'school_bus' | 'shuttle' | 'service_provider' | 'other';
  notes: string | null;
  status: 'active' | 'suspended';
  notify_all_on_entry: boolean;
  max_daily_entries: number | null;
  allowed_windows: Array<{ days: string[]; from: string; until: string }>;
  created_at: string;
  updated_at: string;
}

export interface CreateTrustedOperatorBody {
  name: string;
  operatorType: 'school_bus' | 'shuttle' | 'service_provider' | 'other';
  notes?: string;
  notifyAllOnEntry: boolean;
  maxDailyEntries?: number;
  allowedWindows: Array<{ days: string[]; from: string; until: string }>;
}

export interface TrustedOperatorEntryResult {
  status: 'granted' | 'denied';
  reason?: 'OPERATOR_SUSPENDED' | 'OUTSIDE_ALLOWED_WINDOW' | 'DAILY_LIMIT_REACHED';
  accessEventId?: string;
  nextWindow?: { day: string; from: string; until: string };
  count?: number;
  limit?: number;
}

export interface TrustedOperatorSubscription {
  id: string;
  trusted_operator_id: string;
  created_at: string;
}

// PM endpoints
export async function getTrustedOperators(propertyId: string): Promise<TrustedOperator[]> {
  const { data } = await apiClient.get<TrustedOperator[]>(
    `/properties/${propertyId}/trusted-operators`,
  );
  return data;
}

export async function createTrustedOperator(
  propertyId: string,
  body: CreateTrustedOperatorBody,
): Promise<TrustedOperator> {
  const { data } = await apiClient.post<TrustedOperator>(
    `/properties/${propertyId}/trusted-operators`,
    body,
  );
  return data;
}

export async function updateTrustedOperator(
  propertyId: string,
  id: string,
  body: Partial<CreateTrustedOperatorBody>,
): Promise<TrustedOperator> {
  const { data } = await apiClient.patch<TrustedOperator>(
    `/properties/${propertyId}/trusted-operators/${id}`,
    body,
  );
  return data;
}

export async function suspendTrustedOperator(propertyId: string, id: string): Promise<void> {
  await apiClient.post(`/properties/${propertyId}/trusted-operators/${id}/suspend`);
}

export async function reinstateTrustedOperator(propertyId: string, id: string): Promise<void> {
  await apiClient.post(`/properties/${propertyId}/trusted-operators/${id}/reinstate`);
}

export async function deleteTrustedOperator(propertyId: string, id: string): Promise<void> {
  await apiClient.delete(`/properties/${propertyId}/trusted-operators/${id}`);
}

// Guard endpoints
export async function getGuardTrustedOperators(): Promise<TrustedOperator[]> {
  const { data } = await apiClient.get<TrustedOperator[]>('/guard/trusted-operators');
  return data;
}

export async function recordTrustedOperatorEntry(
  operatorId: string,
  body: { vehiclePlate?: string | undefined },
): Promise<TrustedOperatorEntryResult> {
  const { data } = await apiClient.post<TrustedOperatorEntryResult>(
    `/guard/trusted-operators/${operatorId}/entry`,
    body,
  );
  return data;
}

// Resident subscription endpoints
export async function getTrustedOperatorSubscriptions(
  propertyId: string,
): Promise<TrustedOperatorSubscription[]> {
  const { data } = await apiClient.get<TrustedOperatorSubscription[]>(
    `/properties/${propertyId}/trusted-operators/subscriptions`,
  );
  return data;
}

export async function subscribeTrustedOperator(
  propertyId: string,
  operatorId: string,
): Promise<void> {
  await apiClient.post(`/properties/${propertyId}/trusted-operators/${operatorId}/subscription`);
}

export async function unsubscribeTrustedOperator(
  propertyId: string,
  operatorId: string,
): Promise<void> {
  await apiClient.delete(`/properties/${propertyId}/trusted-operators/${operatorId}/subscription`);
}
